import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData, VerifyData, ConvertToGoodReceipt, ConvertPurchaseVerify } from '../../../apiService';
import apiConfig from '../../../apiConfig';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { usePermissions } from '../../../usePermissions';
import {
  Add, Edit, Visibility, Delete, ShoppingCart, FilterList, Refresh, Warning, CheckCircle, Cancel, Close, ExpandMore, ExpandLess, CheckCircleOutline, Assignment, Search
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Collapse, Divider, Grid,
  TextField, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useMediaQuery
} from '@mui/material';
import PurchaseModal from './PurchaseModal';
import VerifyPurchaseModal from './VerifyPurchaseModal';
import './PurchaseIndex.css';

const PurchaseIndex = () => {
  // Session validation
  useSessionValidation();

  const history = useHistory();
  const { companyId, branchId, token, userId, permissions } = useAuth();
  const { canCreateTransaction, canEditTransaction, canDeleteTransaction } = usePermissions();

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Verify modal states
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifyPurchaseData, setVerifyPurchaseData] = useState(null);
  const [verifyPurchaseDetails, setVerifyPurchaseDetails] = useState([]);

  // Data states
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);

  // Inactive modal states
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
  const [inactiveViewData, setInactiveViewData] = useState(null);
  const [inactiveViewDetails, setInactiveViewDetails] = useState([]);

  // Converted PO List modal states
  const [convertedPOModalOpen, setConvertedPOModalOpen] = useState(false);
  const [convertedPOList, setConvertedPOList] = useState([]);
  const [requestCount, setRequestCount] = useState(0);

  // PO to Purchase Create modal states
  const [poToPurchaseModalOpen, setPOToPurchaseModalOpen] = useState(false);
  const [selectedPOData, setSelectedPOData] = useState(null);
  const [selectedPODetails, setSelectedPODetails] = useState([]);
  const [poToPurchaseFormData, setPOToPurchaseFormData] = useState({});

  // PO Details View modal states
  const [poDetailsModalOpen, setPODetailsModalOpen] = useState(false);
  const [poDetailsData, setPODetailsData] = useState(null);
  const [poDetailsItems, setPODetailsItems] = useState([]);

  // Purchase Return modal states
  const [purchaseReturnModalOpen, setPurchaseReturnModalOpen] = useState(false);
  const [purchaseReturnData, setPurchaseReturnData] = useState(null);
  const [purchaseReturnDetails, setPurchaseReturnDetails] = useState([]);
  const [purchaseReturnFormData, setPurchaseReturnFormData] = useState({});

  // Purchase Details View modal states (for bill number click in Purchase Return)
  const [purchaseDetailsViewModalOpen, setPurchaseDetailsViewModalOpen] = useState(false);
  const [purchaseDetailsViewData, setPurchaseDetailsViewData] = useState(null);
  const [purchaseDetailsViewItems, setPurchaseDetailsViewItems] = useState([]);

  // Supplier drill-down modal states
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierPurchases, setSupplierPurchases] = useState([]);
  const [expandedPurchaseId, setExpandedPurchaseId] = useState(null);

  // Purchase drill-down states for animated expansion
  const [expandedPurchaseRowId, setExpandedPurchaseRowId] = useState(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);

  // Mobile detection
  const isMobile = useMediaQuery('(max-width:600px)');

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [purchaseDetails, setPurchaseDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete/Activate confirmation modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  // Convert to Good Receipt confirmation modal
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [convertItemId, setConvertItemId] = useState(null);
  const [convertItemNo, setConvertItemNo] = useState('');

  // Freight + Packing GST state
  const [freightPackingGST, setFreightPackingGST] = useState({ freight: null, packing: null });

  // Watch for changes in selectedPODetails and recalculate totals
  useEffect(() => {
    if (selectedPODetails.length > 0) {
      calculatePOTotals(selectedPODetails);
    }
  }, [selectedPODetails.length]); // Only trigger when items are added/removed

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData();
    }
  }, [companyId, token, fromDate, toDate]);

  // Fetch functions
  const fetchPurchases = async () => {
    try {
      const activeResponse = await fetchData('PurchaseApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));

      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('PurchaseApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive purchases:', inactiveError);
      }

      setPurchases([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      setPurchases([]);
    }
  };

  const fetchSuppliers = async () => {

    try {
      const response = await fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token);
      setSuppliers(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetchData('ItemApi', `/${companyId}`, token);
      setItems(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const fetchMaxBillNo = async () => {
    try {
      const response = await fetchData('PurchaseApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '' };
    }
  };

  const fetchFreightAndPackingGST = async () => {
    try {
      const response = await fetchData('PurchaseApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token);
      const data = response.response_data || response || [];
      let freightData = null, packingData = null;
      if (Array.isArray(data)) {
        freightData = data.find(item => item.Name?.toLowerCase().includes('freight'));
        packingData = data.find(item => item.Name?.toLowerCase().includes('packing'));
      }
      setFreightPackingGST({ freight: freightData, packing: packingData });
      return { freight: freightData, packing: packingData };
    } catch (error) {
      console.error('Error fetching freight and packing GST:', error);
      return { freight: null, packing: null };
    }
  };

  const fetchPurchaseReturnMaxBillNo = async () => {
    try {
      const response = await fetchData('PurchaseReturnApi', `/MaxBillno/${companyId}/${branchId}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || 'DN';
      const finYear = data.FinYear || data.finYear || '';
      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching purchase return max bill number:', error);
      return { billNumber: 1, billPrefix: 'DN', finYear: '' };
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Fetching purchases...');
    try {
      await Promise.allSettled([
        fetchPurchases(),
        fetchSuppliers(),
        fetchItems(),
        fetchFreightAndPackingGST(),
        // Load request count on initial load
        fetchConvertedPOList().then(poList => setRequestCount(poList.length))
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Purchase data specifically for Purchase Return
  const fetchPurchaseForReturn = async (purchaseId) => {
    try {
      const response = await fetchData('PurchaseReturnApi', `/PurchaseData/${companyId}/${purchaseId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching purchase data for return:', error);
      return null;
    }
  };

  // Fetch single Purchase details for view modal
  const fetchSinglePurchase = async (purchaseId) => {
    try {
      const response = await fetchData('PurchaseApi', `/${purchaseId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching single purchase:', error);
      return null;
    }
  };

  // Fetch inactive Purchase details for view modal
  const fetchInactivePurchaseDetails = async (id) => {
    try {
      const response = await fetchData('PurchaseApi', `/InActivePurchaseListDetails/${id}/${branchId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching inactive purchase details:', error);
      return null;
    }
  };

  // Fetch Converted PO List
  const fetchConvertedPOList = async () => {
    try {
      const response = await fetchData('PurchaseApi', `/PurchaseOrderToPurchase/${companyId}/${branchId}`, token);
      return response.response_data || response || [];
    } catch (error) {
      console.error('Error fetching converted PO list:', error);
      return [];
    }
  };

  // Handle Request button click - Open Converted PO List modal
  const handleOpenConvertedPOModal = async () => {
    setModalLoading(true);
    const poList = await fetchConvertedPOList();
    setConvertedPOList(poList);
    setRequestCount(poList.length); // Set the count
    setModalLoading(false);
    setConvertedPOModalOpen(true);
  };

  const handleCloseConvertedPOModal = () => {
    setConvertedPOModalOpen(false);
    setConvertedPOList([]);
  };

  // Fetch PO details for PO to Purchase
  const fetchPODetailsForPurchase = async (poId) => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/${poId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching PO details:', error);
      return null;
    }
  };

  // Handle "To Purchase" button click
  const handleOpenPOToPurchaseModal = async (po) => {
    setModalLoading(true);

    const poDetails = await fetchPODetailsForPurchase(po.Id);

    if (poDetails) {
      const poData = poDetails.PurchaseOrder || poDetails;
      const details = poDetails.PurchaseOrderDetails || [];

      setSelectedPOData(poData);

      // Initialize details with proper calculation structure
      const initializedDetails = details.map(d => ({
        ...d,
        ReceivedQuantity: d.Quantity || 0,
        DamageQuantity: 0,
        StockQuantity: d.Quantity || 0,
        PendingQuantity: d.PendingQuantity || d.Quantity || 0,
        GSTRate: d.VATRate || d.GSTRate || 0,
        VATRate: d.VATRate || d.GSTRate || 0,
        Amount: (d.Quantity || 0) * (d.Rate || 0),
        DiscountPercentage: d.DiscountPercentage || 0,
        DiscountAmount: d.DiscountAmount || 0,
        TaxableAmount: d.TaxableAmount || ((d.Quantity || 0) * (d.Rate || 0) - (d.DiscountAmount || 0)),
        VATAmount: d.VATAmount || d.CGSTAmount || 0,
        CGSTAmount: d.CGSTAmount || (d.VATAmount ? d.VATAmount / 2 : 0),
        SGSTAmount: d.SGSTAmount || (d.VATAmount ? d.VATAmount / 2 : 0),
        IGSTAmount: d.IGSTAmount || 0,
        TotalAmount: d.TotalAmount || ((d.Quantity || 0) * (d.Rate || 0) - (d.DiscountAmount || 0) + (d.VATAmount || d.CGSTAmount || 0))
      }));

      setSelectedPODetails(initializedDetails);

      // Get VAT rates for freight and packing
      const defaultFreightVATRate = parseFloat(freightPackingGST?.freight?.VATRate || freightPackingGST?.freight?.GSTRate) || 0;
      const defaultPackingVATRate = parseFloat(freightPackingGST?.packing?.VATRate || freightPackingGST?.packing?.GSTRate) || 0;
      const maxItemVATRate = initializedDetails.length > 0 ? Math.max(...initializedDetails.map(d => parseFloat(d.VATRate || d.GSTRate) || 0)) : 0;

      const effectiveFreightVATRate = Math.max(defaultFreightVATRate, maxItemVATRate);
      const effectivePackingVATRate = Math.max(defaultPackingVATRate, maxItemVATRate);

      // Calculate initial freight and packing VAT amounts
      const freightAmount = poData.FreightAmount || 0;
      const packingAmount = poData.PackingAmount || 0;
      const freightVATAmount = (freightAmount * effectiveFreightVATRate) / 100;
      const packingVATAmount = (packingAmount * effectivePackingVATRate) / 100;

      // Calculate totals from items
      const itemsTotal = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.TotalAmount) || 0), 0);
      const itemsAmount = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.Amount) || 0), 0);
      const itemsDiscount = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.DiscountAmount) || 0), 0);
      const itemsTaxable = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.TaxableAmount) || 0), 0);
      const itemsVAT = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.VATAmount) || 0), 0);

      // Calculate grand total
      const tcsAmount = poData.TCSAmount || 0;
      const grandTotal = itemsTotal + freightAmount + freightVATAmount + packingAmount + packingVATAmount + tcsAmount;
      const roundOff = Math.round(grandTotal) - grandTotal;
      const finalTotal = Math.round(grandTotal);

      const initialFormData = {
        PurchaseOrderId: poData.Id,
        PurchaseOrderNo: poData.PurchaseOrderNo,
        PurchaseOrderDate: poData.PurchaseOrderDate?.split('T')[0] || '',
        MasterId: poData.MasterId,
        MasterName: poData.MasterName || '',
        CurrencyName: poData.CurrencyName || '',
        CurrencyTypeId: poData.CurrencyTypeId || null,
        BillNumber: '',
        BillDate: new Date().toISOString().split('T')[0],
        ClearingDate: new Date().toISOString().split('T')[0],
        VATCalculationType: poData.VATCalculationType || poData.GstCalculationType || 1,
        VATCalculationTypeName: poData.VATCalculationTypeName || poData.GstCalculationTypeName || 'Excluding Tax',
        Amount: Math.round(itemsAmount * 100) / 100,
        DiscountAmount: Math.round(itemsDiscount * 100) / 100,
        TaxableAmount: Math.round(itemsTaxable * 100) / 100,
        VATAmount: Math.round(itemsVAT * 100) / 100,
        FreightAmount: freightAmount,
        FreightVATRate: effectiveFreightVATRate,
        FreightVATAmount: Math.round(freightVATAmount * 100) / 100,
        FreightGSTRate: effectiveFreightVATRate,
        FreightGSTAmount: Math.round(freightVATAmount * 100) / 100,
        PackingAmount: packingAmount,
        PackingVATRate: effectivePackingVATRate,
        PackingVATAmount: Math.round(packingVATAmount * 100) / 100,
        PackingGSTRate: effectivePackingVATRate,
        PackingGSTAmount: Math.round(packingVATAmount * 100) / 100,
        TCSAmount: tcsAmount,
        RoundOff: Math.round(roundOff * 100) / 100,
        TotalAmount: finalTotal,
        PaidAmount: 0,
        BalanceAmount: finalTotal,
        Remarks: '',
        AdditionalRemarks: ''
      };

      setPOToPurchaseFormData(initialFormData);
    }

    setModalLoading(false);
    setConvertedPOModalOpen(false);
    setPOToPurchaseModalOpen(true);
  };

  const handleClosePOToPurchaseModal = () => {
    setPOToPurchaseModalOpen(false);
    setSelectedPOData(null);
    setSelectedPODetails([]);
    setPOToPurchaseFormData({});
  };

  // Handle PO Details View Modal
  const handleOpenPODetailsModal = async (poData) => {
    setModalLoading(true);

    try {
      const poDetails = await fetchPODetailsForPurchase(poData.Id);

      if (poDetails) {
        const poInfo = poDetails.PurchaseOrder || poDetails;
        const details = poDetails.PurchaseOrderDetails || [];

        setPODetailsData(poInfo);
        setPODetailsItems(details);
      } else {
        setPODetailsData(poData);
        setPODetailsItems([]);
      }
    } catch (error) {
      console.error('Error fetching PO details:', error);
      setPODetailsData(poData);
      setPODetailsItems([]);
    }

    setModalLoading(false);
    setPODetailsModalOpen(true);
  };

  const handleClosePODetailsModal = () => {
    setPODetailsModalOpen(false);
    setPODetailsData(null);
    setPODetailsItems([]);
  };

  // Handle Purchase Return Modal
  const handleOpenPurchaseReturnModal = async (purchaseItem) => {
    setModalLoading(true);

    try {
      console.log('Opening Purchase Return Modal for:', purchaseItem);
      const purchaseReturnData = await fetchPurchaseForReturn(purchaseItem.Id);
      console.log('Fetched purchase return data:', purchaseReturnData);

      if (purchaseReturnData) {
        const purchaseData = purchaseReturnData.PurchaseReturn || purchaseReturnData;
        const details = purchaseReturnData.PurchaseReturnDetails || purchaseReturnData.PurchaseDetails || [];

        console.log('Purchase data:', purchaseData);
        console.log('Purchase data keys:', Object.keys(purchaseData));
        console.log('BillNumber:', purchaseData.BillNumber);
        console.log('PurchaseNo:', purchaseData.PurchaseNo);
        console.log('Purchase return details array:', details);
        console.log('Details length:', details.length);

        setPurchaseReturnData(purchaseData);

        // Initialize return details with return quantities set to 0
        const initializedReturnDetails = details.map(d => ({
          ...d,
          ReturnQuantity: d.Quantity || 0, // Use existing return quantity if available
          ReturnAmount: d.Amount || 0,
          ReturnDiscountAmount: d.DiscountAmount || 0,
          ReturnTaxableAmount: d.TaxableAmount || 0,
          ReturnVATAmount: d.VATAmount || d.CGSTAmount || 0,
          ReturnTotalAmount: d.TotalAmount || 0,
          MaxReturnQuantity: d.PurchaseQuantity || 0, // Use PurchaseQuantity as max returnable quantity
          // Keep original fields for reference
          OriginalQuantity: d.Quantity || 0,
          OriginalAmount: d.Amount || 0,
          OriginalTaxableAmount: d.TaxableAmount || 0,
          OriginalVATAmount: d.VATAmount || d.CGSTAmount || 0,
          OriginalTotalAmount: d.TotalAmount || 0
        }));

        console.log('Final initialized return details:', initializedReturnDetails);
        setPurchaseReturnDetails(initializedReturnDetails);

        // Fetch max bill number for purchase return
        const maxBillData = await fetchPurchaseReturnMaxBillNo();
        // Format: prefix/billno/finyear (same as PurchaseReturnModal)
        const returnNumber = `${maxBillData.billPrefix}/${maxBillData.billNumber}/${maxBillData.finYear}`;

        // Initialize form data for return
        // Calculate VAT rates for freight and packing
        const defaultVATRate = 5; // Default VAT rate
        const purchaseDetails = details || [];
        const maxItemVATRate = purchaseDetails.length > 0 ? Math.max(...purchaseDetails.map(d => parseFloat(d.VATRate) || 0)) : 0;

        // Use highest VAT rate if > 5%, otherwise use default 5%
        const effectiveVATRate = maxItemVATRate > defaultVATRate ? maxItemVATRate : defaultVATRate;

        console.log('VAT Rate Calculation:', {
          defaultVATRate,
          maxItemVATRate,
          effectiveVATRate,
          purchaseDetails: purchaseDetails.map(d => ({ ItemName: d.ItemName, VATRate: d.VATRate }))
        });

        setPurchaseReturnFormData({
          PurchaseId: purchaseData.PurchaseId || purchaseData.Id,
          PurchaseNo: purchaseData.PurchaseNo || purchaseData.BillNumber || '', // Try both fields
          PurchaseBillNumber: purchaseData.BillNumber || purchaseData.PurchaseNo || '', // Keep for display
          PurchaseBillDate: purchaseData.PurchaseDate || purchaseData.BillDate,
          ReturnDate: new Date().toISOString().split('T')[0],
          ReturnNumber: returnNumber, // Auto-generated return number
          MasterId: purchaseData.MasterId,
          MasterName: purchaseData.MasterName,
          InvoiceTypeId: purchaseData.InvoiceTypeId || 0,
          VATNumber: purchaseData.VATNumber || purchaseData.VATNumber || '',
          BranchId: purchaseData.BranchId || branchId,
          VATCalculationType: purchaseData.VATCalculationType || purchaseData.VATCalculationType || 1, // 1 = Excluding Tax, 2 = Including Tax
          VATCalculationTypeName: purchaseData.VATCalculationTypeName || purchaseData.VATCalculationTypeName || 'Excluding Tax',
          CurrencyTypeId: purchaseData.CurrencyTypeId || purchaseItem.CurrencyTypeId || null,
          CurrencyName: purchaseData.CurrencyName || purchaseItem.CurrencyName || '',
          Amount: 0,
          DiscountAmount: 0,
          TaxableAmount: 0,
          VATAmount: 0,
          FreightAmount: 0,
          FreightVATRate: effectiveVATRate,
          FreightVATAmount: 0,
          PackingAmount: 0,
          PackingVATRate: effectiveVATRate,
          PackingVATAmount: 0,
          TCSAmount: 0,
          RoundOff: 0,
          TotalAmount: 0,
          PaidAmount: 0,
          BalanceAmount: 0,
          Remarks: ''
        });
      } else {
        setPurchaseReturnData(purchaseItem);
        setPurchaseReturnDetails([]);
        setPurchaseReturnFormData({});
      }
    } catch (error) {
      console.error('Error fetching purchase details for return:', error);
      setPurchaseReturnData(purchaseItem);
      setPurchaseReturnDetails([]);
      setPurchaseReturnFormData({});
    }

    setModalLoading(false);
    setPurchaseReturnModalOpen(true);
  };

  const handleClosePurchaseReturnModal = () => {
    setPurchaseReturnModalOpen(false);
    setPurchaseReturnData(null);
    setPurchaseReturnDetails([]);
    setPurchaseReturnFormData({});
  };

  // Handle Purchase Details View Modal (for bill number click)
  const handleOpenPurchaseDetailsViewModal = async (purchaseData) => {
    setModalLoading(true);

    try {
      // If called from Purchase Return modal, use PurchaseId instead of Id
      const purchaseId = purchaseData.PurchaseId || purchaseData.Id;
      console.log('Opening Purchase Details View for Purchase ID:', purchaseId);
      console.log('Purchase Data:', purchaseData);

      const purchaseDetails = await fetchSinglePurchase(purchaseId);

      if (purchaseDetails) {
        const purchaseInfo = purchaseDetails.Purchase || purchaseDetails;
        const details = purchaseDetails.PurchaseDetails || [];

        console.log('Fetched Purchase Info:', purchaseInfo);
        console.log('Fetched Purchase Details:', details);

        setPurchaseDetailsViewData(purchaseInfo);
        setPurchaseDetailsViewItems(details);
      } else {
        setPurchaseDetailsViewData(purchaseData);
        setPurchaseDetailsViewItems([]);
      }
    } catch (error) {
      console.error('Error fetching purchase details:', error);
      setPurchaseDetailsViewData(purchaseData);
      setPurchaseDetailsViewItems([]);
    }

    setModalLoading(false);
    setPurchaseDetailsViewModalOpen(true);
  };

  const handleClosePurchaseDetailsViewModal = () => {
    setPurchaseDetailsViewModalOpen(false);
    setPurchaseDetailsViewData(null);
    setPurchaseDetailsViewItems([]);
  };

  // Handle Purchase Return detail changes
  const handlePurchaseReturnDetailChange = (index, field, value) => {
    const updatedDetails = [...purchaseReturnDetails];

    // Handle non-numeric fields first
    if (!['ReturnQuantity', 'DiscountPercentage', 'DiscountAmount'].includes(field)) {
      updatedDetails[index] = { ...updatedDetails[index], [field]: value };
      setPurchaseReturnDetails(updatedDetails);
      return;
    }

    const detail = updatedDetails[index];

    // Handle Return Quantity change
    if (field === 'ReturnQuantity') {
      // Allow empty value
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: 0 };
        calculateReturnTotals(updatedDetails);
        return;
      }

      // Strict numeric validation
      const validNumberRegex = /^[0-9]*\.?[0-9]*$/;
      if (!validNumberRegex.test(value)) {
        return;
      }

      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) {
        return;
      }

      // Check if return quantity doesn't exceed purchased quantity
      const maxReturnQty = updatedDetails[index].MaxReturnQuantity || 0;
      if (numValue > maxReturnQty) {
        return;
      }

      const returnQty = numValue;
      const rate = parseFloat(detail.Rate) || 0;
      const vatRate = parseFloat(detail.VATRate) || 0;
      const discountPct = parseFloat(detail.DiscountPercentage) || 0;

      // Calculate return amounts
      const returnAmount = returnQty * rate;
      const returnDiscountAmount = (returnAmount * discountPct) / 100;
      const returnTaxableAmount = returnAmount - returnDiscountAmount;
      const returnVATAmount = (returnTaxableAmount * vatRate) / 100;

      updatedDetails[index] = {
        ...updatedDetails[index],
        ReturnQuantity: returnQty,
        ReturnAmount: Math.round(returnAmount * 100) / 100,
        ReturnDiscountAmount: Math.round(returnDiscountAmount * 100) / 100,
        ReturnTaxableAmount: Math.round(returnTaxableAmount * 100) / 100,
        ReturnVATAmount: Math.round(returnVATAmount * 100) / 100,
        ReturnTotalAmount: Math.round((returnTaxableAmount + returnVATAmount) * 100) / 100
      };

      setPurchaseReturnDetails(updatedDetails);
      calculateReturnTotals(updatedDetails);
    }

    // Handle Discount Percentage change
    else if (field === 'DiscountPercentage') {
      const numValue = parseFloat(value) || 0;
      if (numValue < 0 || numValue > 100) {
        return;
      }

      const returnQty = parseFloat(detail.ReturnQuantity) || 0;
      const rate = parseFloat(detail.Rate) || 0;
      const vatRate = parseFloat(detail.VATRate || detail.VATRate) || 0;

      const returnAmount = returnQty * rate;
      const returnDiscountAmount = (returnAmount * numValue) / 100;
      const returnTaxableAmount = returnAmount - returnDiscountAmount;
      const returnVATAmount = (returnTaxableAmount * vatRate) / 100;

      updatedDetails[index] = {
        ...updatedDetails[index],
        DiscountPercentage: numValue,
        ReturnDiscountAmount: Math.round(returnDiscountAmount * 100) / 100,
        ReturnTaxableAmount: Math.round(returnTaxableAmount * 100) / 100,
        ReturnVATAmount: Math.round(returnVATAmount * 100) / 100,
        ReturnTotalAmount: Math.round((returnTaxableAmount + returnVATAmount) * 100) / 100
      };

      setPurchaseReturnDetails(updatedDetails);
      calculateReturnTotals(updatedDetails);
    }

    // Handle Discount Amount change
    else if (field === 'DiscountAmount') {
      const numValue = parseFloat(value) || 0;

      const returnQty = parseFloat(detail.ReturnQuantity) || 0;
      const rate = parseFloat(detail.Rate) || 0;
      const vatRate = parseFloat(detail.VATRate || detail.VATRate) || 0;

      const returnAmount = returnQty * rate;
      const discountPct = returnAmount > 0 ? (numValue / returnAmount) * 100 : 0;
      const returnTaxableAmount = returnAmount - numValue;
      const returnVATAmount = (returnTaxableAmount * vatRate) / 100;

      updatedDetails[index] = {
        ...updatedDetails[index],
        DiscountPercentage: Math.round(discountPct * 100) / 100,
        ReturnDiscountAmount: Math.round(numValue * 100) / 100,
        ReturnTaxableAmount: Math.round(returnTaxableAmount * 100) / 100,
        ReturnVATAmount: Math.round(returnVATAmount * 100) / 100,
        ReturnTotalAmount: Math.round((returnTaxableAmount + returnVATAmount) * 100) / 100
      };

      setPurchaseReturnDetails(updatedDetails);
      calculateReturnTotals(updatedDetails);
    }
  };

  // Calculate Purchase Return totals
  const calculateReturnTotals = (details) => {
    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.ReturnAmount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.ReturnDiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.ReturnTaxableAmount) || 0),
      VATAmount: acc.VATAmount + (parseFloat(d.ReturnVATAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.ReturnTotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, VATAmount: 0, TotalAmount: 0 });

    // Calculate additional charges (proportional to return amount vs original amount)
    const originalAmount = parseFloat(purchaseReturnData?.Amount) || 1;
    const returnRatio = totals.Amount / originalAmount;

    const freightAmount = Math.round((parseFloat(purchaseReturnData?.FreightAmount) || 0) * returnRatio * 100) / 100;
    const freightVATAmount = Math.round((parseFloat(purchaseReturnData?.FreightVATAmount || purchaseReturnData?.FreightGSTAmount) || 0) * returnRatio * 100) / 100;
    const packingAmount = Math.round((parseFloat(purchaseReturnData?.PackingAmount) || 0) * returnRatio * 100) / 100;
    const packingVATAmount = Math.round((parseFloat(purchaseReturnData?.PackingVATAmount || purchaseReturnData?.PackingGSTAmount) || 0) * returnRatio * 100) / 100;
    const tcsAmount = Math.round((parseFloat(purchaseReturnData?.TCSAmount) || 0) * returnRatio * 100) / 100;

    const grandTotal = totals.TotalAmount + freightAmount + freightVATAmount + packingAmount + packingVATAmount + tcsAmount;
    const roundOff = Math.round(grandTotal) - grandTotal;
    const finalTotal = Math.round(grandTotal);

    // Update form data with calculated totals
    setPurchaseReturnFormData(prev => ({
      ...prev,
      Amount: Math.round(totals.Amount * 100) / 100,
      DiscountAmount: Math.round(totals.DiscountAmount * 100) / 100,
      TaxableAmount: Math.round(totals.TaxableAmount * 100) / 100,
      VATAmount: Math.round(totals.VATAmount * 100) / 100,
      FreightAmount: freightAmount,
      FreightVATAmount: freightVATAmount,
      PackingAmount: packingAmount,
      PackingVATAmount: packingVATAmount,
      TCSAmount: tcsAmount,
      RoundOff: Math.round(roundOff * 100) / 100,
      TotalAmount: finalTotal
    }));
  };

  // Handle Purchase Return form field changes
  const handlePurchaseReturnFormChange = (field, value) => {
    setPurchaseReturnFormData(prev => {
      const updatedFormData = {
        ...prev,
        [field]: value
      };

      // If freight or packing amount changes, recalculate GST and totals
      if (field === 'FreightAmount' || field === 'PackingAmount') {
        const freightAmount = field === 'FreightAmount' ? parseFloat(value) || 0 : parseFloat(prev.FreightAmount) || 0;
        const packingAmount = field === 'PackingAmount' ? parseFloat(value) || 0 : parseFloat(prev.PackingAmount) || 0;

        // Use stored GST rates from form data
        const freightVATRate = parseFloat(prev.FreightVATRate) || 18;
        const packingVATRate = parseFloat(prev.PackingVATRate) || 18;

        // Calculate GST amounts
        const freightVATAmount = (freightAmount * freightVATRate) / 100;
        const packingVATAmount = (packingAmount * packingVATRate) / 100;

        // Update GST amounts
        updatedFormData.FreightVATAmount = Math.round(freightVATAmount * 100) / 100;
        updatedFormData.PackingVATAmount = Math.round(packingVATAmount * 100) / 100;

        // Recalculate total: items total + freight + freight GST + packing + packing GST + TCS
        const itemsTotal = parseFloat(prev.TaxableAmount) || 0;
        const itemsVATTotal = (parseFloat(prev.VATAmount) || 0) + (parseFloat(prev.IGSTAmount) || 0);
        const tcsAmount = parseFloat(prev.TCSAmount) || 0;

        const grandTotal = itemsTotal + itemsVATTotal + freightAmount + freightVATAmount + packingAmount + packingVATAmount + tcsAmount;
        const roundOff = Math.round(grandTotal) - grandTotal;
        const finalTotal = Math.round(grandTotal);

        updatedFormData.RoundOff = Math.round(roundOff * 100) / 100;
        updatedFormData.TotalAmount = finalTotal;

        // Recalculate balance
        const paidAmount = parseFloat(prev.PaidAmount) || 0;
        updatedFormData.BalanceAmount = finalTotal - paidAmount;
      }

      // If TCS amount changes, recalculate totals
      if (field === 'TCSAmount') {
        const tcsAmount = parseFloat(value) || 0;
        updatedFormData.TCSAmount = tcsAmount; // Ensure TCS amount is set

        const itemsTotal = parseFloat(prev.TaxableAmount) || 0;
        const itemsVATTotal = (parseFloat(prev.VATAmount) || 0) + (parseFloat(prev.IGSTAmount) || 0);
        const freightAmount = parseFloat(prev.FreightAmount) || 0;
        const freightVATAmount = parseFloat(prev.FreightVATAmount) || 0;
        const packingAmount = parseFloat(prev.PackingAmount) || 0;
        const packingVATAmount = parseFloat(prev.PackingVATAmount) || 0;

        const grandTotal = itemsTotal + itemsVATTotal + freightAmount + freightVATAmount + packingAmount + packingVATAmount + tcsAmount;
        const roundOff = Math.round(grandTotal) - grandTotal;
        const finalTotal = Math.round(grandTotal);

        updatedFormData.RoundOff = Math.round(roundOff * 100) / 100;
        updatedFormData.TotalAmount = finalTotal;

        // Recalculate balance
        const paidAmount = parseFloat(prev.PaidAmount) || 0;
        updatedFormData.BalanceAmount = finalTotal - paidAmount;
      }

      // If paid amount changes, recalculate balance
      if (field === 'PaidAmount') {
        const paidAmount = parseFloat(value) || 0;
        const totalAmount = parseFloat(prev.TotalAmount) || 0;
        updatedFormData.BalanceAmount = totalAmount - paidAmount;
      }

      return updatedFormData;
    });
  };

  // Save Purchase Return
  const handleSavePurchaseReturn = async () => {
    // Validation
    const validationErrors = [];

    if (!purchaseReturnFormData.ReturnNumber?.trim()) {
      validationErrors.push('Return Number is required');
    }

    if (!purchaseReturnFormData.ReturnDate) {
      validationErrors.push('Return Date is required');
    }

    // Check if there are items with return quantity
    const itemsWithReturnQty = purchaseReturnDetails.filter(item => (item.ReturnQuantity || 0) > 0);
    if (itemsWithReturnQty.length === 0) {
      validationErrors.push('At least one item must have return quantity greater than 0');
    }

    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setLoadingMessage('Creating purchase return...');

    try {
      const dataToSend = {
        PurchaseReturn: {
          Id: 0,
          CompanyId: companyId,
          BranchId: purchaseReturnFormData.BranchId || branchId,
          PurchaseId: purchaseReturnFormData.PurchaseId,
          PurchaseNo: purchaseReturnFormData.PurchaseBillNumber,
          PurchaseDate: purchaseReturnFormData.PurchaseBillDate,
          BillNumber: purchaseReturnFormData.ReturnNumber,
          BillDate: purchaseReturnFormData.ReturnDate,
          MasterId: purchaseReturnFormData.MasterId,
          MasterName: purchaseReturnFormData.MasterName,
          InvoiceTypeId: purchaseReturnFormData.InvoiceTypeId || 0,
          VATNumber: purchaseReturnFormData.VATNumber || '',
          VATCalculationType: parseInt(purchaseReturnFormData.VATCalculationType) || 1,
          VATCalculationTypeName: purchaseReturnFormData.VATCalculationTypeName || 'Excluding Tax',
          CurrencyTypeId: parseInt(purchaseReturnFormData.CurrencyTypeId) || 0,
          CurrencyName: purchaseReturnFormData.CurrencyName || '',
          Amount: parseFloat(purchaseReturnFormData.Amount) || 0,
          DiscountAmount: parseFloat(purchaseReturnFormData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(purchaseReturnFormData.TaxableAmount) || 0,
          VATAmount: parseFloat(purchaseReturnFormData.VATAmount) || 0,
          // SGSTAmount: parseFloat(purchaseReturnFormData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(purchaseReturnFormData.IGSTAmount) || 0,
          FreightAmount: parseFloat(purchaseReturnFormData.FreightAmount) || 0,
          FreightVATRate: parseFloat(purchaseReturnFormData.FreightVATRate) || 0,
          FreightVATAmount: parseFloat(purchaseReturnFormData.FreightVATAmount) || 0,
          PackingAmount: parseFloat(purchaseReturnFormData.PackingAmount) || 0,
          PackingVATRate: parseFloat(purchaseReturnFormData.PackingVATRate) || 0,
          PackingVATAmount: parseFloat(purchaseReturnFormData.PackingVATAmount) || 0,
          TCSAmount: parseFloat(purchaseReturnFormData.TCSAmount) || 0,
          RoundOff: parseFloat(purchaseReturnFormData.RoundOff) || 0,
          TotalAmount: parseFloat(purchaseReturnFormData.TotalAmount) || 0,
          PaidAmount: parseFloat(purchaseReturnFormData.PaidAmount) || 0,
          BalanceAmount: parseFloat(purchaseReturnFormData.BalanceAmount) || 0,
          Remarks: purchaseReturnFormData.Remarks || '',
          IsActive: true,
          CreatedBy: userId
        },
        PurchaseReturnDetails: itemsWithReturnQty.map(d => ({
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          PurchaseReturnId: 0, // Will be set by backend
          ItemId: d.ItemId,
          ItemName: d.ItemName || '',
          BatchNumber: d.BatchNumber || '',
          PurchaseId: purchaseReturnFormData.PurchaseId,
          PurchaseQuantity: parseFloat(d.PurchaseQuantity) || 0, // Original purchased quantity
          Quantity: parseFloat(d.ReturnQuantity) || 0, // Return quantity
          ReturnStockQuantity: parseFloat(d.ReturnQuantity) || 0, // Good return quantity
          Rate: parseFloat(d.Rate) || 0,
          Amount: parseFloat(d.ReturnAmount) || 0,
          DiscountPercentage: parseFloat(d.DiscountPercentage) || 0,
          DiscountAmount: parseFloat(d.ReturnDiscountAmount) || 0,
          HSNCode: d.HSNCode || '',
          VATRate: parseFloat(d.VATRate || d.GSTRate) || 0,
          TaxableAmount: parseFloat(d.ReturnTaxableAmount) || 0,
          VATAmount: parseFloat(d.ReturnVATAmount) || 0,
          TotalAmount: parseFloat(d.ReturnTotalAmount) || 0,
          IsActive: true,
          CreatedBy: userId
        }))
      };

      await postData('PurchaseReturnApi', '', dataToSend, token);
      setSuccess('Purchase return created successfully!');
      handleClosePurchaseReturnModal();
      await fetchPurchases(); // Refresh the purchase list

      // Redirect to Purchase Return page
      setTimeout(() => {
        history.push('/PurchaseReturnAgainstIndex');
      }, 1500);
    } catch (error) {
      console.error('Error creating purchase return:', error);
      setError('Failed to create purchase return.');
    }

    setLoading(false);
  };

  // Handle PO to Purchase form field change
  const handlePOToPurchaseFormChange = (field, value) => {
    // Update the form data first
    setPOToPurchaseFormData(prev => {
      const updatedFormData = { ...prev, [field]: value };

      // If GST Type changes, recalculate all items
      if (field === 'VATCalculationType') {
        const isIncludingTax = parseInt(value) === 2;

        // Recalculate all item details based on new GST type
        const recalculatedDetails = selectedPODetails.map(detail => {
          const receivedQty = Math.max(0, parseFloat(detail.ReceivedQuantity) || 0);
          const rate = Math.max(0, parseFloat(detail.Rate) || 0);
          const gstRate = Math.max(0, parseFloat(detail.GSTRate) || 0);

          let taxableAmount, gstAmount, amount;

          if (isIncludingTax) {
            // Rate includes GST - calculate backwards
            const totalWithGst = receivedQty * rate;
            taxableAmount = totalWithGst / (1 + gstRate / 100);
            amount = taxableAmount;

            const discountPct = parseFloat(detail.DiscountPercentage) || 0;
            const discountAmount = (taxableAmount * discountPct) / 100;

            taxableAmount = taxableAmount - discountAmount;
            gstAmount = (taxableAmount * gstRate) / 100;

            return {
              ...detail,
              Amount: Math.round(amount * 100) / 100,
              DiscountAmount: Math.round(discountAmount * 100) / 100,
              TaxableAmount: Math.round(taxableAmount * 100) / 100,
              CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
              SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
              IGSTAmount: 0,
              TotalAmount: Math.round((taxableAmount + gstAmount) * 100) / 100
            };
          } else {
            // Rate excludes GST - normal calculation
            amount = receivedQty * rate;
            const discountPct = parseFloat(detail.DiscountPercentage) || 0;
            const discountAmount = (amount * discountPct) / 100;

            taxableAmount = amount - discountAmount;
            gstAmount = (taxableAmount * gstRate) / 100;

            return {
              ...detail,
              Amount: Math.round(amount * 100) / 100,
              DiscountAmount: Math.round(discountAmount * 100) / 100,
              TaxableAmount: Math.round(taxableAmount * 100) / 100,
              CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
              SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
              IGSTAmount: 0,
              TotalAmount: Math.round((taxableAmount + gstAmount) * 100) / 100
            };
          }
        });

        // Update the details
        setSelectedPODetails(recalculatedDetails);

        // Recalculate totals with new details
        setTimeout(() => {
          calculatePOTotals(recalculatedDetails);
        }, 0);
      }

      // If freight or packing amount changes, recalculate immediately
      if (field === 'FreightAmount' || field === 'PackingAmount') {
        // Get GST rates
        const defaultFreightGSTRate = parseFloat(freightPackingGST?.freight?.GSTRate) || 0;
        const defaultPackingGSTRate = parseFloat(freightPackingGST?.packing?.GSTRate) || 0;
        const maxItemGSTRate = selectedPODetails.length > 0 ? Math.max(...selectedPODetails.map(d => parseFloat(d.GSTRate) || 0)) : 0;

        const effectiveFreightGSTRate = Math.max(defaultFreightGSTRate, maxItemGSTRate);
        const effectivePackingGSTRate = Math.max(defaultPackingGSTRate, maxItemGSTRate);

        // Calculate GST amounts
        const freightAmount = field === 'FreightAmount' ? parseFloat(value) || 0 : parseFloat(updatedFormData.FreightAmount) || 0;
        const packingAmount = field === 'PackingAmount' ? parseFloat(value) || 0 : parseFloat(updatedFormData.PackingAmount) || 0;

        const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;
        const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;

        // Update GST rates and amounts
        updatedFormData.FreightGSTRate = effectiveFreightGSTRate;
        updatedFormData.FreightGSTAmount = Math.round(freightGSTAmount * 100) / 100;
        updatedFormData.PackingGSTRate = effectivePackingGSTRate;
        updatedFormData.PackingGSTAmount = Math.round(packingGSTAmount * 100) / 100;

        // Recalculate total
        const itemsTotal = selectedPODetails.reduce((sum, d) => sum + (parseFloat(d.TotalAmount) || 0), 0);
        const tcsAmount = parseFloat(updatedFormData.TCSAmount) || 0;
        const grandTotal = itemsTotal + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
        const roundOff = Math.round(grandTotal) - grandTotal;
        const finalTotal = Math.round(grandTotal);
        const paidAmount = parseFloat(updatedFormData.PaidAmount) || 0;

        updatedFormData.RoundOff = Math.round(roundOff * 100) / 100;
        updatedFormData.TotalAmount = finalTotal;
        updatedFormData.BalanceAmount = finalTotal - paidAmount;
      }

      return updatedFormData;
    });

    // Recalculate totals for other fields
    if (['TCSAmount', 'PaidAmount'].includes(field)) {
      setTimeout(() => {
        calculatePOTotals(selectedPODetails);
      }, 0);
    }
  };

  // Handle PO to Purchase detail change
  const handlePOToPurchaseDetailChange = (index, field, value) => {
    const updatedDetails = [...selectedPODetails];

    // Handle non-numeric fields first
    if (['ItemId', 'ItemName', 'HSNCode'].includes(field)) {
      updatedDetails[index] = { ...updatedDetails[index], [field]: value };
      setSelectedPODetails(updatedDetails);
      // Always recalculate when ItemId changes (item selection)
      if (field === 'ItemId' || field === 'ItemName') {
        calculatePOTotals(updatedDetails);
      }
      return;
    }

    // Enhanced validation for numeric inputs
    if (['ReceivedQuantity', 'Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'GSTRate'].includes(field)) {
      // Allow empty value
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: '' };
        setSelectedPODetails(updatedDetails);
        calculatePOTotals(updatedDetails);
        return;
      }

      // Strict numeric validation - only allow numbers and decimal point
      const validNumberRegex = /^[0-9]*\.?[0-9]*$/;
      if (!validNumberRegex.test(value)) {
        // Invalid input - don't update
        return;
      }

      // Check for valid decimal number
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        // Invalid number - don't update
        return;
      }

      // Additional validation for specific fields
      if (['ReceivedQuantity', 'Quantity', 'Rate'].includes(field) && numValue < 0) {
        // Negative values not allowed for these fields
        return;
      }

      if (['DiscountPercentage'].includes(field) && (numValue < 0 || numValue > 100)) {
        // Discount percentage should be between 0-100
        return;
      }

      if (['GSTRate'].includes(field) && (numValue < 0 || numValue > 100)) {
        // GST rate should be between 0-100
        return;
      }
    }

    updatedDetails[index] = { ...updatedDetails[index], [field]: value };

    // Recalculate item totals when relevant fields change
    if (['ReceivedQuantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'GSTRate'].includes(field)) {
      const detail = updatedDetails[index];
      const receivedQty = Math.max(0, parseFloat(detail.ReceivedQuantity) || 0);
      const rate = Math.max(0, parseFloat(detail.Rate) || 0);
      const gstRate = Math.max(0, parseFloat(detail.GSTRate) || 0);
      const isIncludingTax = (poToPurchaseFormData.VATAmountCalculationType || 1) === 2;

      let taxableAmount, gstAmount, amount;

      if (isIncludingTax) {
        // Rate includes GST - calculate backwards
        const totalWithGst = receivedQty * rate;
        taxableAmount = totalWithGst / (1 + gstRate / 100);
        amount = taxableAmount;

        let discountAmount = 0, discountPct = 0;
        if (field === 'DiscountAmount') {
          discountAmount = Math.max(0, parseFloat(value) || 0);
          discountPct = taxableAmount > 0 ? (discountAmount / taxableAmount) * 100 : 0;
        } else {
          discountPct = parseFloat(detail.DiscountPercentage) || 0;
          discountAmount = (taxableAmount * discountPct) / 100;
        }

        taxableAmount = taxableAmount - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;

        updatedDetails[index] = {
          ...updatedDetails[index],
          Amount: Math.round(amount * 100) / 100,
          DiscountPercentage: Math.round(discountPct * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: Math.round(taxableAmount * 100) / 100,
          CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
          SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
          IGSTAmount: 0,
          TotalAmount: Math.round((taxableAmount + gstAmount) * 100) / 100,
          StockQuantity: receivedQty,
          DamageQuantity: parseFloat(detail.DamageQuantity) || 0
        };
      } else {
        // Rate excludes GST - normal calculation
        amount = receivedQty * rate;

        let discountAmount = 0, discountPct = 0;
        if (field === 'DiscountAmount') {
          discountAmount = Math.max(0, parseFloat(value) || 0);
          discountPct = amount > 0 ? (discountAmount / amount) * 100 : 0;
        } else {
          discountPct = parseFloat(detail.DiscountPercentage) || 0;
          discountAmount = (amount * discountPct) / 100;
        }

        taxableAmount = amount - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;

        updatedDetails[index] = {
          ...updatedDetails[index],
          Amount: Math.round(amount * 100) / 100,
          DiscountPercentage: Math.round(discountPct * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: Math.round(taxableAmount * 100) / 100,
          CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
          SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
          IGSTAmount: 0,
          TotalAmount: Math.round((taxableAmount + gstAmount) * 100) / 100,
          StockQuantity: receivedQty,
          DamageQuantity: parseFloat(detail.DamageQuantity) || 0
        };
      }
    }

    setSelectedPODetails(updatedDetails);
    calculatePOTotals(updatedDetails);
  };

  // Calculate PO to Purchase totals
  const calculatePOTotals = (details) => {
    console.log('Calculating PO totals for details:', details);

    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.Amount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.TaxableAmount) || 0),
      CGSTAmount: acc.CGSTAmount + (parseFloat(d.CGSTAmount) || 0),
      SGSTAmount: acc.SGSTAmount + (parseFloat(d.SGSTAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.IGSTAmount) || 0),
      VATAmount: acc.VATAmount + (parseFloat(d.VATAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.TotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, VATAmount: 0, TotalAmount: 0 });

    console.log('Calculated item totals:', totals);

    // Get current freight and packing amounts from form data
    const freightAmount = parseFloat(poToPurchaseFormData.FreightAmount) || 0;
    const packingAmount = parseFloat(poToPurchaseFormData.PackingAmount) || 0;

    // Get GST rates - use default rates from freight/packing GST data or highest item GST rate
    const defaultFreightGSTRate = parseFloat(freightPackingGST?.freight?.GSTRate || freightPackingGST?.freight?.VATRate) || 0;
    const defaultPackingGSTRate = parseFloat(freightPackingGST?.packing?.GSTRate || freightPackingGST?.packing?.VATRate) || 0;
    const maxItemGSTRate = details.length > 0 ? Math.max(...details.map(d => parseFloat(d.GSTRate) || parseFloat(d.VATRate) || 0)) : 0;

    const effectiveFreightGSTRate = Math.max(defaultFreightGSTRate, maxItemGSTRate);
    const effectivePackingGSTRate = Math.max(defaultPackingGSTRate, maxItemGSTRate);

    // Calculate GST amounts
    const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;
    const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;

    const tcsAmount = parseFloat(poToPurchaseFormData.TCSAmount) || 0;

    // Calculate grand total
    const grandTotal = totals.TotalAmount + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
    const roundOff = Math.round(grandTotal) - grandTotal;
    const finalTotal = Math.round(grandTotal);

    const paidAmount = parseFloat(poToPurchaseFormData.PaidAmount) || 0;
    const balanceAmount = finalTotal - paidAmount;

    // Update form data with calculated totals
    setPOToPurchaseFormData(prev => {
      const updatedFormData = {
        ...prev,
        Amount: Math.round(totals.Amount * 100) / 100,
        DiscountAmount: Math.round(totals.DiscountAmount * 100) / 100,
        TaxableAmount: Math.round(totals.TaxableAmount * 100) / 100,
        CGSTAmount: Math.round(totals.CGSTAmount * 100) / 100,
        SGSTAmount: Math.round(totals.SGSTAmount * 100) / 100,
        IGSTAmount: Math.round(totals.IGSTAmount * 100) / 100,
        FreightGSTRate: effectiveFreightGSTRate,
        FreightGSTAmount: Math.round(freightGSTAmount * 100) / 100,
        PackingGSTRate: effectivePackingGSTRate,
        PackingGSTAmount: Math.round(packingGSTAmount * 100) / 100,
        RoundOff: Math.round(roundOff * 100) / 100,
        TotalAmount: finalTotal,
        BalanceAmount: balanceAmount
      };

      console.log('Updated form data:', updatedFormData);
      return updatedFormData;
    });
  };

  // Save PO to Purchase with validation
  const handleSavePOToPurchase = async () => {
    // Validation
    const validationErrors = [];

    // Check required fields
    if (!poToPurchaseFormData.BillNumber?.trim()) {
      validationErrors.push('Bill Number is required');
    }

    if (!poToPurchaseFormData.BillDate) {
      validationErrors.push('Bill Date is required');
    }

    // Check if there are items
    if (!selectedPODetails || selectedPODetails.length === 0) {
      validationErrors.push('At least one item is required');
    }

    // Validate each item
    selectedPODetails.forEach((item, index) => {
      if (!item.ItemId) {
        validationErrors.push(`Item ${index + 1}: Please select an item`);
      }

      if (!item.ReceivedQuantity || parseFloat(item.ReceivedQuantity) <= 0) {
        validationErrors.push(`Item ${index + 1}: Received quantity must be greater than 0`);
      }

      if (!item.Rate || parseFloat(item.Rate) <= 0) {
        validationErrors.push(`Item ${index + 1}: Rate must be greater than 0`);
      }
    });

    // Check if total amount is greater than 0
    if (!poToPurchaseFormData.TotalAmount || parseFloat(poToPurchaseFormData.TotalAmount) <= 0) {
      validationErrors.push('Total amount must be greater than 0');
    }

    // Show validation errors
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setLoadingMessage('Creating purchase from PO...');

    try {
      // Calculate total quantity from all details
      const totalQuantity = selectedPODetails.reduce((sum, d) => sum + (parseFloat(d.ReceivedQuantity) || 0), 0);

      // Calculate VATAmount (sum of all GST/VAT from items)
      const vatAmount = selectedPODetails.reduce((sum, d) => {
        const cgst = parseFloat(d.CGSTAmount) || 0;
        const sgst = parseFloat(d.SGSTAmount) || 0;
        const igst = parseFloat(d.IGSTAmount) || 0;
        const itemVat = (cgst + sgst + igst) > 0 ? (cgst + sgst + igst) : (parseFloat(d.VATAmount) || 0);
        return sum + itemVat;
      }, 0);

      const dataToSend = {
        Purchase: {
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          MasterId: poToPurchaseFormData.MasterId,
          MasterName: poToPurchaseFormData.MasterName,
          InvoiceTypeId: suppliers.find(s => (s.MasterId || s.Id) === poToPurchaseFormData.MasterId)?.InvoiceTypeId || 0,
          VATNumber: suppliers.find(s => (s.MasterId || s.Id) === poToPurchaseFormData.MasterId)?.VATNumber || suppliers.find(s => (s.MasterId || s.Id) === poToPurchaseFormData.MasterId)?.GSTNumber || '',
          CurrencyTypeId: poToPurchaseFormData.CurrencyTypeId || null,
          CurrencyName: poToPurchaseFormData.CurrencyName || '',
          VATCalculationType: poToPurchaseFormData.VATCalculationType || 1,
          VATCalculationTypeName: poToPurchaseFormData.VATCalculationTypeName || poToPurchaseFormData.VATtCalculationTypeName || 'Excluding Tax',
          PurchaseOrderId: poToPurchaseFormData.PurchaseOrderId,
          PurchaseOrderNo: poToPurchaseFormData.PurchaseOrderNo,
          PurchaseOrderDate: poToPurchaseFormData.PurchaseOrderDate,
          BillNumber: poToPurchaseFormData.BillNumber,
          BillDate: poToPurchaseFormData.BillDate,
          ClearingDate: poToPurchaseFormData.ClearingDate || null,
          TotalQuantity: totalQuantity,
          Amount: parseFloat(poToPurchaseFormData.Amount) || 0,
          DiscountAmount: parseFloat(poToPurchaseFormData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(poToPurchaseFormData.TaxableAmount) || 0,
          VATAmount: vatAmount,
          CGSTAmount: parseFloat(poToPurchaseFormData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(poToPurchaseFormData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(poToPurchaseFormData.IGSTAmount) || 0,
          FreightAmount: parseFloat(poToPurchaseFormData.FreightAmount) || 0,
          FreightVATRate: parseFloat(poToPurchaseFormData.FreightGSTRate) || parseFloat(poToPurchaseFormData.FreightVATRate) || 0,
          FreightVATAmount: parseFloat(poToPurchaseFormData.FreightGSTAmount) || parseFloat(poToPurchaseFormData.FreightVATAmount) || 0,
          PackingAmount: parseFloat(poToPurchaseFormData.PackingAmount) || 0,
          PackingVATRate: parseFloat(poToPurchaseFormData.PackingGSTRate) || parseFloat(poToPurchaseFormData.PackingVATRate) || 0,
          PackingVATAmount: parseFloat(poToPurchaseFormData.PackingGSTAmount) || parseFloat(poToPurchaseFormData.PackingVATAmount) || 0,
          TCSAmount: parseFloat(poToPurchaseFormData.TCSAmount) || 0,
          RoundOff: parseFloat(poToPurchaseFormData.RoundOff) || 0,
          TotalAmount: parseFloat(poToPurchaseFormData.TotalAmount) || 0,
          PaidAmount: parseFloat(poToPurchaseFormData.PaidAmount) || 0,
          BalanceAmount: (parseFloat(poToPurchaseFormData.TotalAmount) || 0) - (parseFloat(poToPurchaseFormData.PaidAmount) || 0),
          Remarks: poToPurchaseFormData.Remarks || '',
          AdditionalRemarks: poToPurchaseFormData.AdditionalRemarks || '',
          IsActive: true,
          CreatedBy: userId
        },
        PurchaseDetails: selectedPODetails.map(d => {
          const gstRate = parseFloat(d.GSTRate) || parseFloat(d.VATRate) || 0;
          const cgst = parseFloat(d.CGSTAmount) || 0;
          const sgst = parseFloat(d.SGSTAmount) || 0;
          const igst = parseFloat(d.IGSTAmount) || 0;
          // Use CGST+SGST+IGST if available (from recalculation), otherwise fall back to VATAmount
          const itemVATAmount = (cgst + sgst + igst) > 0 ? (cgst + sgst + igst) : (parseFloat(d.VATAmount) || 0);

          return {
            Id: 0,
            CompanyId: companyId,
            BranchId: branchId,
            ItemId: d.ItemId,
            ItemName: d.ItemName || '',
            PurchaseOrderId: poToPurchaseFormData.PurchaseOrderId,
            OrderQuantity: d.Quantity || 0,
            ReceivedQuantity: parseFloat(d.ReceivedQuantity) || 0,
            DamageQuantity: parseFloat(d.DamageQuantity) || 0,
            StockQuantity: parseFloat(d.StockQuantity) || 0,
            Rate: parseFloat(d.Rate) || 0,
            Amount: parseFloat(d.Amount) || 0,
            DiscountPercentage: parseFloat(d.DiscountPercentage) || 0,
            DiscountAmount: parseFloat(d.DiscountAmount) || 0,
            HSNCode: d.HSNCode || '',
            VATRate: gstRate,
            GSTRate: gstRate,
            TaxableAmount: parseFloat(d.TaxableAmount) || 0,
            VATAmount: itemVATAmount,
            CGSTAmount: cgst,
            SGSTAmount: sgst,
            IGSTAmount: igst,
            TotalAmount: parseFloat(d.TotalAmount) || 0,
            IsActive: true,
            CreatedBy: userId
          };
        })
      };

      await postData('PurchaseApi', '', dataToSend, token);
      setSuccess('Purchase created successfully from PO!');
      handleClosePOToPurchaseModal();
      await fetchPurchases();
    } catch (error) {
      console.error('Error creating purchase from PO:', error);
      setError('Failed to create purchase from PO.');
    }

    setLoading(false);
  };

  // Activate handlers
  const handleOpenActivateModal = (id, billNo) => {
    setActivateItemId(id);
    setActivateItemNo(billNo);
    setActivateModalOpen(true);
  };

  const handleCloseActivateModal = () => {
    setActivateModalOpen(false);
    setActivateItemId(null);
    setActivateItemNo('');
  };

  const handleActivate = async () => {
    if (!activateItemId) return;
    setLoading(true);
    handleCloseActivateModal();
    setLoadingMessage('Activating purchase...');
    try {
      await activateData('PurchaseApi', `${activateItemId}`, token);
      setSuccess('Purchase activated successfully!');
      await fetchAllData();
    } catch (error) {
      console.error('Error activating record:', error);
      setError('Failed to activate purchase.');
    }
    setLoading(false);
  };

  // Inactive view modal - separate styled modal like PurchaseOrder
  const handleOpenInactiveModal = async (item) => {
    setModalLoading(true);

    const freshData = await fetchInactivePurchaseDetails(item.Id);

    if (freshData) {
      const purchaseData = freshData.Purchase || freshData;
      const details = freshData.PurchaseDetails || [];
      setInactiveViewData(purchaseData);
      setInactiveViewDetails(details);
    } else {
      setInactiveViewData(item);
      setInactiveViewDetails([]);
    }

    setModalLoading(false);
    setInactiveModalOpen(true);
  };

  const handleCloseInactiveModal = () => {
    setInactiveModalOpen(false);
    setInactiveViewData(null);
    setInactiveViewDetails([]);
  };

  // Modal handlers
  const handleOpenModal = async (action, itemOrId = null) => {
    setModalAction(action);
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;

    if ((action === 'view' || action === 'edit') && itemId) {
      setModalLoading(true);
      const freshData = await fetchSinglePurchase(itemId);

      if (freshData) {
        const p = freshData.Purchase || freshData;
        const details = freshData.PurchaseDetails || [];
        const supplierName = p.MasterName || suppliers.find(s => (s.MasterId || s.Id) === p.MasterId)?.Name || '';
        const supplier = suppliers.find(s => (s.MasterId || s.Id) === p.MasterId);
        const invoiceTypeName = p.InvoiceTypeName || supplier?.InvoiceTypeName || '';

        setSelectedItem(p);
        setFormData({
          Id: p.Id,
          CompanyId: p.CompanyId || companyId,
          BranchId: p.BranchId || branchId,
          MasterId: p.MasterId,
          MasterName: supplierName,
          InvoiceTypeId: p.InvoiceTypeId || 0,
          InvoiceTypeName: invoiceTypeName,
          CurrencyTypeId: p.CurrencyTypeId || null,
          CurrencyName: p.CurrencyName || '',
          VATNumber: p.VATNumber || p.GSTNumber || '',
          VATCalculationType: p.VATCalculationType || 1,
          VATCalculationTypeName: p.VATCalculationTypeName || 'Excluding Tax',
          // PO Reference
          PurchaseOrderId: p.PurchaseOrderId || null,
          PurchaseOrderNo: p.PurchaseOrderNo || '',
          PurchaseOrderDate: p.PurchaseOrderDate?.split('T')[0] || '',
          // Purchase Details
          BillDate: p.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          ClearingDate: p.ClearingDate?.split('T')[0] || '',
          BillNumber: p.BillNumber || '',
          // Calculation Fields
          TotalQuantity: p.TotalQuantity || 0,
          Amount: p.Amount || 0,
          VATAmount: p.VATAmount || (p.CGSTAmount || 0) + (p.SGSTAmount || 0) + (p.IGSTAmount || 0),
          TaxableAmount: p.TaxableAmount || 0,
          FreightAmount: p.FreightAmount || 0,
          FreightVATRate: p.FreightVATRate || p.FreightGSTRate || 0,
          FreightVATAmount: p.FreightVATAmount || p.FreightGSTAmount || 0,
          PackingAmount: p.PackingAmount || 0,
          PackingVATRate: p.PackingVATRate || p.PackingGSTRate || 0,
          PackingVATAmount: p.PackingVATAmount || p.PackingGSTAmount || 0,
          TCSAmount: p.TCSAmount || 0,
          DiscountAmount: p.DiscountAmount || 0,
          RoundOff: p.RoundOff || 0,
          TotalAmount: p.TotalAmount || 0,
          PaidAmount: p.PaidAmount || 0,
          BalanceAmount: p.BalanceAmount || 0,
          Remarks: p.Remarks || '',
          IsActive: p.IsActive ?? true,
          IsVerified: p.IsVerified ?? false,
          IsConvertToGoodReceipt: p.IsConvertToGoodReceipt ?? false,
          CreatedBy: p.CreatedBy,
          CreatedByName: p.CreatedByName || '',
          CreatedDate: p.CreatedDate,
          ModifiedBy: p.ModifiedBy,
          ModifiedByName: p.ModifiedByName || '',
          ModifiedDate: p.ModifiedDate
        });

        setPurchaseDetails(details.map(d => ({
          Id: d.Id || 0,
          PurchaseId: d.PurchaseId || 0,
          CompanyId: d.CompanyId || companyId,
          BranchId: d.BranchId || branchId,
          ItemId: d.ItemId,
          ItemName: d.ItemName || items.find(i => i.Id === d.ItemId)?.Name || '',
          BatchNumber: d.BatchNumber || '',
          PurchaseOrderId: d.PurchaseOrderId || null,
          // Quantity fields
          OrderQuantity: d.OrderQuantity || 0,
          ReceivedQuantity: d.ReceivedQuantity || 0,
          BilledQuantity: d.BilledQuantity || 0,
          DamageQuantity: d.DamageQuantity || 0,
          ExcessQuantity: d.ExcessQuantity || 0,
          LessQuantity: d.LessQuantity || 0,
          StockQuantity: d.StockQuantity || 0,
          ReturnDamageQuantity: d.ReturnDamageQuantity || 0,
          ReturnStockQuantity: d.ReturnStockQuantity || 0,
          // Calculation fields
          Rate: d.Rate || 0,
          Amount: d.Amount || 0,
          DiscountPercentage: d.DiscountPercentage || 0,
          DiscountAmount: d.DiscountAmount || 0,
          HSNCode: d.HSNCode || '',
          VATRate: d.VATRate || d.GSTRate || 0,
          TaxableAmount: d.TaxableAmount || 0,
          VATAmount: d.VATAmount || (d.CGSTAmount || 0) + (d.SGSTAmount || 0) + (d.IGSTAmount || 0),
          TotalAmount: d.TotalAmount || 0,
          IsActive: d.IsActive ?? true,
          CreatedBy: d.CreatedBy,
          CreatedDate: d.CreatedDate,
          ModifiedBy: d.ModifiedBy,
          ModifiedDate: d.ModifiedDate
        })));
      } else {
        setSelectedItem(null);
        setFormData({});
        setPurchaseDetails([]);
      }
      setModalLoading(false);
    } else {
      // Create mode
      setModalLoading(true);
      const maxBillData = await fetchMaxBillNo();
      const gstData = await fetchFreightAndPackingGST();
      const freightGSTRate = gstData.freight?.GSTRate || 0;
      const packingGSTRate = gstData.packing?.GSTRate || 0;

      setSelectedItem(null);
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        MasterId: '',
        InvoiceTypeId: 0,
        CurrencyTypeId: null,
        CurrencyName: '',
        GSTNumber: '',
        PurchaseOrderId: null,
        PurchaseOrderNo: '',
        PurchaseOrderDate: '',
        BillDate: new Date().toISOString().split('T')[0],
        ClearingDate: new Date().toISOString().split('T')[0],
        BillNumber: '',
        TotalQuantity: 0,
        Amount: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        FreightAmount: 0,
        FreightGSTRate: freightGSTRate,
        FreightGSTAmount: 0,
        PackingAmount: 0,
        PackingGSTRate: packingGSTRate,
        PackingGSTAmount: 0,
        TCSAmount: 0,
        DiscountAmount: 0,
        RoundOff: 0,
        TotalAmount: 0,
        PaidAmount: 0,
        BalanceAmount: 0,
        Remarks: '',
        IsActive: true,
        IsVerified: false,
        IsConvertToGoodReceipt: false
      });
      setPurchaseDetails([]);
      setModalLoading(false);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setPurchaseDetails([]);
    window.location.reload();
  };

  // Save handler
  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} purchase...`);

      const selectedSupplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(formData.MasterId));
      const masterId = selectedSupplier?.MasterId || selectedSupplier?.Id || 0;
      const masterName = selectedSupplier?.Name || '';
      const invoiceTypeId = selectedSupplier?.InvoiceTypeId || formData.InvoiceTypeId || 0;
      const vatNumber = selectedSupplier?.VATNumber || selectedSupplier?.GSTNumber || selectedSupplier?.GSTNo || formData.VATNumber || '';

      const totalQuantity = purchaseDetails.reduce((sum, d) => sum + (parseFloat(d.ReceivedQuantity) || 0), 0);

      const dataToSend = {
        Purchase: {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          CompanyId: companyId,
          BranchId: formData.BranchId || branchId,
          MasterId: masterId,
          MasterName: masterName,
          InvoiceTypeId: invoiceTypeId,
          CurrencyTypeId: formData.CurrencyTypeId || null,
          CurrencyName: formData.CurrencyName || '',
          VATNumber: vatNumber,
          VATCalculationType: formData.VATCalculationType || 1,
          VATCalculationTypeName: formData.VATCalculationTypeName || 'Excluding Tax',
          // PO Reference
          PurchaseOrderId: formData.PurchaseOrderId || null,
          PurchaseOrderNo: formData.PurchaseOrderNo || '',
          PurchaseOrderDate: formData.PurchaseOrderDate || null,
          // Purchase Details
          BillDate: formData.BillDate || new Date().toISOString().split('T')[0],
          ClearingDate: formData.ClearingDate || null,
          BillNumber: formData.BillNumber || '',
          // Calculation Fields
          TotalQuantity: totalQuantity,
          Amount: parseFloat(formData.Amount) || 0,
          DiscountAmount: parseFloat(formData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(formData.TaxableAmount) || 0,
          VATAmount: parseFloat(formData.VATAmount) || 0,
          FreightAmount: parseFloat(formData.FreightAmount) || 0,
          FreightVATRate: parseFloat(formData.FreightVATRate) || 0,
          FreightVATAmount: parseFloat(formData.FreightVATAmount) || 0,
          PackingAmount: parseFloat(formData.PackingAmount) || 0,
          PackingVATRate: parseFloat(formData.PackingVATRate) || 0,
          PackingVATAmount: parseFloat(formData.PackingVATAmount) || 0,
          TCSAmount: parseFloat(formData.TCSAmount) || 0,
          RoundOff: Math.round((parseFloat(formData.RoundOff) || 0) * 100) / 100,
          TotalAmount: parseFloat(formData.TotalAmount) || 0,
          PaidAmount: parseFloat(formData.PaidAmount) || 0,
          BalanceAmount: parseFloat(formData.BalanceAmount) || 0,
          Remarks: formData.Remarks || '',
          IsActive: formData.IsActive ?? true,
          IsVerified: formData.IsVerified ?? false,
          IsConvertToGoodReceipt: formData.IsConvertToGoodReceipt ?? false,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          CreatedDate: modalAction === 'edit' ? formData.CreatedDate : null,
          ModifiedBy: modalAction === 'edit' ? userId : null
        },
        PurchaseDetails: purchaseDetails.map(d => {
          const selectedItem = items.find(i => i.Id === parseInt(d.ItemId));
          return {
            Id: modalAction === 'edit' ? (d.Id || 0) : 0,
            PurchaseId: modalAction === 'edit' ? formData.Id : 0,
            CompanyId: companyId,
            BranchId: formData.BranchId || branchId,
            ItemId: parseInt(d.ItemId) || 0,
            ItemName: selectedItem?.Name || d.ItemName || '',
            BatchNumber: d.BatchNumber || '',
            PurchaseOrderId: d.PurchaseOrderId || null,
            // Quantity fields
            OrderQuantity: parseFloat(d.OrderQuantity) || 0,
            ReceivedQuantity: parseFloat(d.ReceivedQuantity) || 0,
            BilledQuantity: parseFloat(d.BilledQuantity) || 0,
            DamageQuantity: parseFloat(d.DamageQuantity) || 0,
            ExcessQuantity: parseFloat(d.ExcessQuantity) || 0,
            LessQuantity: parseFloat(d.LessQuantity) || 0,
            StockQuantity: parseFloat(d.StockQuantity) || 0,
            ReturnDamageQuantity: parseFloat(d.ReturnDamageQuantity) || 0,
            ReturnStockQuantity: parseFloat(d.ReturnStockQuantity) || 0,
            // Calculation fields
            Rate: parseFloat(d.Rate) || 0,
            Amount: parseFloat(d.Amount) || 0,
            DiscountPercentage: parseFloat(d.DiscountPercentage) || 0,
            DiscountAmount: parseFloat(d.DiscountAmount) || 0,
            HSNCode: d.HSNCode || '',
            VATRate: parseFloat(d.VATRate) || 0,
            TaxableAmount: parseFloat(d.TaxableAmount) || 0,
            VATAmount: parseFloat(d.VATAmount) || 0,
            TotalAmount: parseFloat(d.TotalAmount) || 0,
            IsActive: d.IsActive ?? true,
            CreatedBy: modalAction === 'create' ? userId : d.CreatedBy,
            CreatedDate: modalAction === 'edit' ? d.CreatedDate : null,
            ModifiedBy: modalAction === 'edit' ? userId : null
          };
        })
      };

      if (modalAction === 'create') {
        await postData('PurchaseApi', '', dataToSend, token);
        setSuccess('Purchase created successfully!');
      } else if (modalAction === 'edit') {
        if (!formData.Id) throw new Error('Purchase ID is missing');
        await updateData('PurchaseApi', formData.Id, dataToSend, token);
        setSuccess('Purchase updated successfully!');
      }

      handleCloseModal();
      await fetchPurchases();
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} purchase. Please try again.`);
      setLoading(false);
    }
  };

  // Supplier drill-down handler
  const handleSupplierClick = async (masterId, supplierName) => {
    setLoading(true);
    setLoadingMessage('Fetching supplier purchases...');
    try {
      const response = await fetchData('PurchaseApi', `/PurchaseHistoryByMaster/${masterId}/${branchId}`, token);
      const responseData = response.response_data || response || [];
      let purchasesList = [], purchaseDetailsList = [];
      if (Array.isArray(responseData) && responseData.length > 0) {
        purchasesList = responseData[0].Purchase || [];
        purchaseDetailsList = responseData[0].PurchaseDetails || [];
      }
      const purchasesWithDetails = purchasesList.map(p => ({
        ...p,
        PurchaseDetails: purchaseDetailsList.filter(d => d.PurchaseId === p.Id)
      }));
      setSelectedSupplier({ id: masterId, name: supplierName });
      setSupplierPurchases(purchasesWithDetails);
      setExpandedPurchaseId(null);
      setSupplierModalOpen(true);
    } catch (error) {
      console.error('Error fetching supplier purchases:', error);
      const filteredPurchases = purchases.filter(p => p.MasterId === masterId);
      setSelectedSupplier({ id: masterId, name: supplierName });
      setSupplierPurchases(filteredPurchases);
      setSupplierModalOpen(true);
    }
    setLoading(false);
  };

  const handleCloseSupplierModal = () => {
    setSupplierModalOpen(false);
    setSelectedSupplier(null);
    setSupplierPurchases([]);
    setExpandedPurchaseId(null);
  };

  const togglePurchaseExpand = (purchaseId) => {
    setExpandedPurchaseId(expandedPurchaseId === purchaseId ? null : purchaseId);
  };

  // Handle purchase row drill-down toggle with animation
  const togglePurchaseRowExpand = (purchaseId) => {
    setExpandedPurchaseRowId(expandedPurchaseRowId === purchaseId ? null : purchaseId);
  };

  // Delete handlers
  const handleOpenDeleteModal = (id, billNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(billNo);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;
    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('PurchaseApi', deleteItemId, token);
      setSuccess('Purchase deleted successfully!');
      await fetchPurchases();
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete purchase.');
    }
    setLoading(false);
  };

  // Convert to Good Receipt handlers
  const handleOpenConvertModal = (id, billNo) => {
    setConvertItemId(id);
    setConvertItemNo(billNo);
    setConvertModalOpen(true);
  };

  const handleCloseConvertModal = () => {
    setConvertModalOpen(false);
    setConvertItemId(null);
    setConvertItemNo('');
  };

  const handleToGoodReceipt = async () => {
    if (!convertItemId) return;
    setLoading(true);
    handleCloseConvertModal();
    setLoadingMessage('Converting to Good Receipt...');
    try {
      await ConvertToGoodReceipt('PurchaseApi', convertItemId, token);
      setSuccess('Purchase converted to Good Receipt successfully!');
      handleCloseModal(); // Close the modal after successful conversion
      await fetchPurchases();
    } catch (error) {
      console.error('Error converting to Good Receipt:', error);
      setError('Failed to convert purchase to Good Receipt');
    }
    setLoading(false);
  };

  const handleVerifyFromModal = async (purchaseId) => {
    // Find the purchase item by ID
    const purchaseItem = purchases.find(p => p.Id === purchaseId);
    if (purchaseItem) {
      await handleOpenVerifyModal(purchaseItem);
    }
  };

  // Handle Update & Save from Good Receipt Modal (same as edit function)
  const handleUpdateFromGoodReceipt = async (purchaseId, updatedPurchaseDetails) => {
    if (!updatedPurchaseDetails || !verifyPurchaseData) {
      setError('Purchase details are required for update.');
      return;
    }

    setLoading(true);
    setLoadingMessage('Updating Good Receipt details...');

    try {
      // Use the same logic as handleSave but for updating from Good Receipt modal
      const selectedSupplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(verifyPurchaseData.MasterId));
      const masterId = selectedSupplier?.MasterId || selectedSupplier?.Id || 0;
      const masterName = selectedSupplier?.Name || '';
      const invoiceTypeId = selectedSupplier?.InvoiceTypeId || verifyPurchaseData.InvoiceTypeId || 0;
      const vatNumber = selectedSupplier?.VATNumber || selectedSupplier?.GSTNumber || selectedSupplier?.GSTNo || verifyPurchaseData.VATNumber || '';

      const totalQuantity = updatedPurchaseDetails.reduce((sum, d) => sum + (parseFloat(d.ReceivedQuantity) || 0), 0);

      const dataToSend = {
        Purchase: {
          Id: verifyPurchaseData.Id,
          CompanyId: companyId,
          BranchId: verifyPurchaseData.BranchId || branchId,
          MasterId: masterId,
          MasterName: masterName,
          InvoiceTypeId: invoiceTypeId,
          CurrencyTypeId: verifyPurchaseData.CurrencyTypeId || null,
          CurrencyName: verifyPurchaseData.CurrencyName || '',
          VATNumber: vatNumber,
          VATCalculationType: verifyPurchaseData.VATCalculationType || 1,
          VATCalculationTypeName: verifyPurchaseData.VATCalculationTypeName || 'Excluding Tax',
          // PO Reference
          PurchaseOrderId: verifyPurchaseData.PurchaseOrderId || null,
          PurchaseOrderNo: verifyPurchaseData.PurchaseOrderNo || '',
          PurchaseOrderDate: verifyPurchaseData.PurchaseOrderDate || null,
          // Purchase Details
          BillDate: verifyPurchaseData.BillDate || new Date().toISOString().split('T')[0],
          ClearingDate: verifyPurchaseData.ClearingDate || null,
          BillNumber: verifyPurchaseData.BillNumber || '',
          // Calculation Fields
          TotalQuantity: totalQuantity,
          Amount: parseFloat(verifyPurchaseData.Amount) || 0,
          DiscountAmount: parseFloat(verifyPurchaseData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(verifyPurchaseData.TaxableAmount) || 0,
          VATAmount: parseFloat(verifyPurchaseData.VATAmount) || 0,
          CGSTAmount: parseFloat(verifyPurchaseData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(verifyPurchaseData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(verifyPurchaseData.IGSTAmount) || 0,
          FreightAmount: parseFloat(verifyPurchaseData.FreightAmount) || 0,
          FreightVATRate: parseFloat(verifyPurchaseData.FreightVATRate) || 0,
          FreightVATAmount: parseFloat(verifyPurchaseData.FreightVATAmount) || 0,
          FreightGSTRate: parseFloat(verifyPurchaseData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(verifyPurchaseData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(verifyPurchaseData.PackingAmount) || 0,
          PackingVATRate: parseFloat(verifyPurchaseData.PackingVATRate) || 0,
          PackingVATAmount: parseFloat(verifyPurchaseData.PackingVATAmount) || 0,
          PackingGSTRate: parseFloat(verifyPurchaseData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(verifyPurchaseData.PackingGSTAmount) || 0,
          TCSAmount: parseFloat(verifyPurchaseData.TCSAmount) || 0,
          RoundOff: Math.round((parseFloat(verifyPurchaseData.RoundOff) || 0) * 100) / 100,
          TotalAmount: parseFloat(verifyPurchaseData.TotalAmount) || 0,
          PaidAmount: parseFloat(verifyPurchaseData.PaidAmount) || 0,
          BalanceAmount: parseFloat(verifyPurchaseData.BalanceAmount) || 0,
          Remarks: verifyPurchaseData.Remarks || '',
          IsActive: verifyPurchaseData.IsActive ?? true,
          IsVerified: verifyPurchaseData.IsVerified ?? false,
          IsConvertToGoodReceipt: true, // Always set to true when updating from Good Receipt modal
          CreatedBy: verifyPurchaseData.CreatedBy,
          CreatedDate: verifyPurchaseData.CreatedDate,
          ModifiedBy: userId
        },
        PurchaseDetails: updatedPurchaseDetails.map(d => {
          const selectedItem = items.find(i => i.Id === parseInt(d.ItemId));
          return {
            Id: d.Id || 0,
            PurchaseId: verifyPurchaseData.Id,
            CompanyId: companyId,
            BranchId: verifyPurchaseData.BranchId || branchId,
            ItemId: parseInt(d.ItemId) || 0,
            ItemName: selectedItem?.Name || d.ItemName || '',
            BatchNumber: d.BatchNumber || '',
            PurchaseOrderId: d.PurchaseOrderId || null,
            // Quantity fields
            OrderQuantity: parseFloat(d.OrderQuantity) || 0,
            ReceivedQuantity: parseFloat(d.ReceivedQuantity) || 0,
            BilledQuantity: parseFloat(d.BilledQuantity) || 0,
            DamageQuantity: parseFloat(d.DamageQuantity) || 0,
            ExcessQuantity: parseFloat(d.ExcessQuantity) || 0,
            LessQuantity: parseFloat(d.LessQuantity) || 0,
            StockQuantity: parseFloat(d.StockQuantity) || 0,
            ReturnDamageQuantity: parseFloat(d.ReturnDamageQuantity) || 0,
            ReturnStockQuantity: parseFloat(d.ReturnStockQuantity) || 0,
            // Calculation fields
            Rate: parseFloat(d.Rate) || 0,
            Amount: parseFloat(d.Amount) || 0,
            DiscountPercentage: parseFloat(d.DiscountPercentage) || 0,
            DiscountAmount: parseFloat(d.DiscountAmount) || 0,
            HSNCode: d.HSNCode || '',
            VATRate: parseFloat(d.VATRate) || 0,
            GSTRate: parseFloat(d.GSTRate) || 0,
            TaxableAmount: parseFloat(d.TaxableAmount) || 0,
            VATAmount: parseFloat(d.VATAmount) || 0,
            CGSTAmount: parseFloat(d.CGSTAmount) || 0,
            SGSTAmount: parseFloat(d.SGSTAmount) || 0,
            IGSTAmount: parseFloat(d.IGSTAmount) || 0,
            TotalAmount: parseFloat(d.TotalAmount) || 0,
            IsActive: d.IsActive ?? true,
            CreatedBy: d.CreatedBy,
            CreatedDate: d.CreatedDate,
            ModifiedBy: userId
          };
        })
      };

      // Use the same update API as edit function
      await updateData('PurchaseApi', verifyPurchaseData.Id, dataToSend, token);
      setSuccess('Good Receipt updated successfully! Changes have been saved.');

      // Refresh the purchase data to show updated values
      const freshData = await fetchSinglePurchase(verifyPurchaseData.Id);
      if (freshData) {
        const p = freshData.Purchase || freshData;
        const details = freshData.PurchaseDetails || [];
        setVerifyPurchaseData(p);
        setVerifyPurchaseDetails(details);
      }

      await fetchPurchases();

    } catch (error) {
      console.error('Error updating purchase:', error);
      setError('Failed to update purchase. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle actual verification (with confirmation)
  const handleActualVerify = async (purchaseId, updatedPurchaseDetails = null) => {
    if (!purchaseId) return;

    setLoading(true);
    setLoadingMessage('Processing Good Receipt verification...');

    try {
      // First update the purchase details if any changes were made
      if (updatedPurchaseDetails && updatedPurchaseDetails.length > 0) {
        await handleUpdateFromGoodReceipt(purchaseId, updatedPurchaseDetails);
      }

      // Then verify the purchase (this will create stock entries)
      await ConvertPurchaseVerify('PurchaseApi', purchaseId, token);
      setSuccess('Good Receipt verified successfully! Stock has been updated and purchase is now complete.');
      handleCloseVerifyModal();
      await fetchPurchases();

    } catch (error) {
      console.error('Error verifying purchase:', error);
      setError('Failed to verify Good Receipt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verify handler
  const handleVerify = async (purchaseId, updatedPurchaseDetails = null) => {
    if (!updatedPurchaseDetails || !verifyPurchaseData) {
      setError('Purchase details are required for verification.');
      return;
    }

    setLoading(true);
    setLoadingMessage('Processing good receipt...');

    try {
      // Prepare the PurchaseVerifyVM structure
      const verifyData = {
        Purchase: {
          ...verifyPurchaseData,
          IsVerified: true,
          ModifiedBy: userId,
          ModifiedDate: new Date().toISOString()
        },
        PurchaseDetails: updatedPurchaseDetails.map(detail => ({
          ...detail,
          ModifiedBy: userId,
          ModifiedDate: new Date().toISOString()
        })),
        // Stock: updatedPurchaseDetails.map(detail => ({
        //   Id: 0, // New stock entry
        //   CompanyId: companyId,
        //   BranchId: branchId,
        //   PurchaseDetailsId: detail.Id,
        //   StockType: 1, // 1 for Purchase
        //   ItemId: detail.ItemId,
        //   UOMId: null, // You can set this if you have UOM data
        //   Quantity: parseFloat(detail.StockQuantity) || 0,
        //   BalanceQuantity: parseFloat(detail.StockQuantity) || 0,
        //   DamageQuantity: parseFloat(detail.DamageQuantity) || 0,
        //   DamageBalanceQuantity: parseFloat(detail.DamageQuantity) || 0,
        //   CGSTRate: parseFloat(detail.GSTRate) / 2 || 0, // Assuming CGST is half of total GST
        //   SGSTRate: parseFloat(detail.GSTRate) / 2 || 0, // Assuming SGST is half of total GST
        //   HSNCode: detail.HSNCode || '',
        //   BatchNumber: detail.BatchNumber || '',
        //   IsReturnable: true, // Default values - you can customize these
        //   ReturnDays: 30,
        //   HasWarranty: false,
        //   WarrantyDays: 0,
        //   StockInDate: new Date().toISOString().split('T')[0],
        //   ReturnDate: null,
        //   WarrantyEndDate: null,
        //   PurchaseRate: parseFloat(detail.Rate) || 0,
        //   IsActive: true,
        //   CreatedBy: userId,
        //   CreatedDate: new Date().toISOString()
        // }))
      };

      // Use the new API endpoint with PurchaseVerifyVM
      const response = await fetch(`${apiConfig.apiUrl}PurchaseApi/${purchaseId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(verifyData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      setSuccess('Good receipt processed successfully with stock updated!');
      handleCloseVerifyModal();
      await fetchPurchases();

    } catch (error) {
      console.error('Error verifying purchase:', error);
      setError('Failed to process good receipt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verify modal handlers
  const handleOpenVerifyModal = async (item) => {
    setModalLoading(true);

    const freshData = await fetchSinglePurchase(item.Id);

    if (freshData) {
      const p = freshData.Purchase || freshData;
      const details = freshData.PurchaseDetails || [];
      const supplierName = p.MasterName || suppliers.find(s => (s.MasterId || s.Id) === p.MasterId)?.Name || '';
      const supplier = suppliers.find(s => (s.MasterId || s.Id) === p.MasterId);
      const invoiceTypeName = p.InvoiceTypeName || supplier?.InvoiceTypeName || '';

      setVerifyPurchaseData({
        Id: p.Id,
        CompanyId: p.CompanyId || companyId,
        BranchId: p.BranchId || branchId,
        MasterId: p.MasterId,
        MasterName: supplierName,
        InvoiceTypeId: p.InvoiceTypeId || 0,
        InvoiceTypeName: invoiceTypeName,
        CurrencyTypeId: p.CurrencyTypeId || null,
        CurrencyName: p.CurrencyName || '',
        VATNumber: p.VATNumber || '',
        BillDate: p.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
        ClearingDate: p.ClearingDate?.split('T')[0] || '',
        BillNumber: p.BillNumber || '',
        TotalQuantity: p.TotalQuantity || 0,
        Amount: p.Amount || 0,
        // CGSTAmount: p.CGSTAmount || 0,
        // SGSTAmount: p.SGSTAmount || 0,
        VATAmount: p.VATAmount,
        IGSTAmount: p.IGSTAmount || 0,
        FreightAmount: p.FreightAmount || 0,
        FreightVATRate: p.FreightVATRate || 0,
        FreightVATAmount: p.FreightVATAmount || 0,
        PackingAmount: p.PackingAmount || 0,
        PackingVATRate: p.PackingVATRate || 0,
        PackingVATAmount: p.PackingVATAmount || 0,
        TCSAmount: p.TCSAmount || 0,
        DiscountAmount: p.DiscountAmount || 0,
        TaxableAmount: p.TaxableAmount || 0,
        RoundOff: p.RoundOff || 0,
        TotalAmount: p.TotalAmount || 0,
        PaidAmount: p.PaidAmount || 0,
        BalanceAmount: p.BalanceAmount || 0,
        Remarks: p.Remarks || '',
        IsActive: p.IsActive ?? true,
        IsVerified: p.IsVerified ?? false,
        CreatedBy: p.CreatedBy,
        CreatedByName: p.CreatedByName || '',
        CreatedDate: p.CreatedDate,
        ModifiedBy: p.ModifiedBy,
        ModifiedByName: p.ModifiedByName || '',
        ModifiedDate: p.ModifiedDate
      });

      setVerifyPurchaseDetails(details.map(d => ({
        Id: d.Id || 0,
        PurchaseId: d.PurchaseId || 0,
        CompanyId: d.CompanyId || companyId,
        BranchId: d.BranchId || branchId,
        ItemId: d.ItemId,
        ItemName: d.ItemName || items.find(i => i.Id === d.ItemId)?.Name || '',
        BatchNumber: d.BatchNumber || '',
        ReceivedQuantity: d.ReceivedQuantity || 0,
        BilledQuantity: d.BilledQuantity || 0,
        DamageQuantity: d.DamageQuantity || 0,
        ExcessQuantity: d.ExcessQuantity || 0,
        LessQuantity: d.LessQuantity || 0,
        StockQuantity: d.StockQuantity || 0,
        ReturnDamageQuantity: d.ReturnDamageQuantity || 0,
        ReturnStockQuantity: d.ReturnStockQuantity || 0,
        Rate: d.Rate || 0,
        Amount: d.Amount || 0,
        DiscountPercentage: d.DiscountPercentage || 0,
        DiscountAmount: d.DiscountAmount || 0,
        HSNCode: d.HSNCode || '',
        VATRate: d.VATRate || 0,
        TaxableAmount: d.TaxableAmount || 0,
        // CGSTAmount: d.CGSTAmount || 0,
        // SGSTAmount: d.SGSTAmount || 0,
        VATAmount: d.VATAmount,
        IGSTAmount: d.IGSTAmount || 0,
        TotalAmount: d.TotalAmount || 0,
        IsActive: d.IsActive ?? true,
        CreatedBy: d.CreatedBy,
        CreatedDate: d.CreatedDate,
        ModifiedBy: d.ModifiedBy,
        ModifiedDate: d.ModifiedDate
      })));
    } else {
      setVerifyPurchaseData(null);
      setVerifyPurchaseDetails([]);
    }

    setModalLoading(false);
    setVerifyModalOpen(true);
  };

  const handleCloseVerifyModal = () => {
    setVerifyModalOpen(false);
    setVerifyPurchaseData(null);
    setVerifyPurchaseDetails([]);
  };

  // Filter and pagination
  const getFilteredData = (data) => {
    if (!Array.isArray(data)) return [];
    let filtered = data;
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.BillNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.MasterName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        suppliers.find(s => s.MasterId === item.MasterId)?.Name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterStatus !== 'all') {
      filtered = filtered.filter(item => filterStatus === 'active' ? item.IsActive : !item.IsActive);
    }
    if (filterVerified !== 'all') {
      if (filterVerified === 'verified') {
        filtered = filtered.filter(item => item.IsConvertToGoodReceipt && !item.IsVerified);
      } else if (filterVerified === 'verified-status') {
        filtered = filtered.filter(item => item.IsVerified);
      } else if (filterVerified === 'pending') {
        filtered = filtered.filter(item => !item.IsConvertToGoodReceipt);
      }
    }
    return filtered;
  };

  const getPaginatedData = (data) => {
    const filtered = getFilteredData(data) || [];
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return {
      data: Array.isArray(filtered) ? filtered.slice(startIndex, endIndex) : [],
      totalCount: Array.isArray(filtered) ? filtered.length : 0,
      totalPages: Math.ceil((Array.isArray(filtered) ? filtered.length : 0) / rowsPerPage),
      startIndex
    };
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); };

  const formatCurrency = (amt, currencyName) => {
    // Auto-detect currency from current context if not explicitly passed
    const name = currencyName || purchaseReturnFormData?.CurrencyName || poToPurchaseFormData?.CurrencyName || formData?.CurrencyName || '';
    const getSymbol = (n) => {
      if (!n) return 'د.إ';
      const lower = n.toLowerCase().trim();
      const symbolMap = {
        'inr': '₹', 'rupee': '₹', 'indian rupee': '₹',
        'usd': '$', 'dollar': '$', 'us dollar': '$',
        'eur': '€', 'euro': '€',
        'gbp': '£', 'pound': '£', 'british pound': '£',
        'aed': 'د.إ', 'dirham': 'د.إ', 'uae dirham': 'د.إ',
        'sar': '﷼', 'riyal': '﷼', 'saudi riyal': '﷼',
        'jpy': '¥', 'yen': '¥',
        'cny': '¥', 'yuan': '¥', 'rmb': '¥',
        'aud': 'A$', 'australian dollar': 'A$',
        'cad': 'C$', 'canadian dollar': 'C$',
        'sgd': 'S$', 'singapore dollar': 'S$',
        'chf': 'CHF', 'swiss franc': 'CHF',
        'myr': 'RM', 'ringgit': 'RM',
        'bdt': '৳', 'taka': '৳',
        'pkr': '₨', 'pakistani rupee': '₨',
        'omr': 'OMR', 'omani rial': 'OMR',
        'qar': 'QR', 'qatari riyal': 'QR',
        'kwd': 'KD', 'kuwaiti dinar': 'KD',
        'bhd': 'BD', 'bahraini dinar': 'BD'
      };
      if (symbolMap[lower]) return symbolMap[lower];
      for (const [key, symbol] of Object.entries(symbolMap)) {
        if (lower.includes(key)) return symbol;
      }
      return n;
    };
    const symbol = getSymbol(name);
    return `${symbol} ${(amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getSupplierName = (item) => {
    if (item.MasterName) return item.MasterName;
    const supplier = suppliers.find(s => (s.MasterId || s.Id) === item.MasterId);
    return supplier?.Name || '-';
  };

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData(purchases);


  return (
    <div style={{
      minWidth: '100%',
      background: '#f5f7fa',
      padding: '10px',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        padding: '12px',
        position: 'relative',
        color: 'black',
        fontFamily: 'Times New Romans',
      }}>
        {loading && <LoadingOverlay open={loading} message={loadingMessage} />}

        <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
        </Snackbar>

        {/* Header Section with Title */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          paddingBottom: '8px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ShoppingCart sx={{ fontSize: 24, color: '#0f3460' }} />
            <div>
              <h4 style={{
                fontSize: '17px',
                fontWeight: '700',
                margin: 0,
                letterSpacing: '-0.3px',
                fontFamily: 'Times New Romans',
                textTransform: 'uppercase'
              }}>
                <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Purchase Management</span>
              </h4>
            </div>
          </div>
        </div>

        {/* Date Filters, Search and Create Button Row - Horizontally Scrollable */}
        <div style={{
          marginBottom: '12px',
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin'
        }}>
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'flex-end',
            minWidth: 'fit-content',
            paddingBottom: '4px'
          }}>
            {/* From Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
              <TextField
                type="date"
                size="small"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                sx={{
                  width: isMobile ? '120px' : '140px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '11px',
                    height: '32px',
                    '&:hover fieldset': { borderColor: '#0f3460' },
                    '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                  },
                }}
              />
            </div>

            {/* To Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
              <TextField
                type="date"
                size="small"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                sx={{
                  width: isMobile ? '120px' : '140px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '11px',
                    height: '32px',
                    '&:hover fieldset': { borderColor: '#0f3460' },
                    '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                  },
                }}
              />
            </div>

            {/* Search - Icon on mobile, always visible on desktop */}
            {isMobile ? (
              searchExpanded ? (
                <TextField
                  size="small"
                  placeholder="Search by Bill No, Supplier..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  autoFocus
                  onBlur={() => {
                    if (!searchTerm) {
                      setSearchExpanded(false);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search style={{ color: '#0f3460', fontSize: '18px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: '250px',
                    width: '350px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '12px',
                      height: '32px',
                      '&:hover fieldset': {
                        borderColor: '#0f3460',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0f3460',
                      },
                    },
                  }}
                />
              ) : (
                <Tooltip title="Search" arrow>
                  <Button
                    onClick={() => setSearchExpanded(true)}
                    style={{
                      minWidth: '32px',
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Search style={{ color: '#0f3460', fontSize: '18px' }} />
                  </Button>
                </Tooltip>
              )
            ) : (
              <TextField
                size="small"
                placeholder="Search by Bill No, Supplier..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search style={{ color: '#0f3460', fontSize: '18px' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  minWidth: '250px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '12px',
                    height: '32px',
                    '&:hover fieldset': {
                      borderColor: '#0f3460',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0f3460',
                    },
                  },
                }}
              />
            )}

            {/* Request Button */}
            <Tooltip title="View purchase order requests" arrow>
              <Button
                variant="contained"
                onClick={handleOpenConvertedPOModal}
                style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  padding: isMobile ? '0' : '4px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '700',
                  boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)',
                  border: 'none',
                  height: '32px',
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  whiteSpace: 'nowrap',
                  minWidth: isMobile ? '32px' : '100px',
                  width: 'auto',
                  position: 'relative'
                }}
              >
                📋
                {!isMobile && <span style={{ fontSize: '12px' }}>Request</span>}
                {requestCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-6px',
                    right: '-6px',
                    background: '#dc2626',
                    color: 'white',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: '700'
                  }}>
                    {requestCount}
                  </span>
                )}
              </Button>
            </Tooltip>

            {/* Create Purchase Button */}
            {canCreateTransaction && (
              <Tooltip title="Create a new purchase" arrow>
                <Button
                  variant="contained"
                  onClick={() => handleOpenModal('create')}
                  className="action-button"
                  style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    color: 'white',
                    padding: isMobile ? '0' : '4px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '700',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                    border: 'none',
                    height: '32px',
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap',
                    minWidth: isMobile ? '32px' : '100px',
                    width: 'auto',
                    marginLeft: 'auto'
                  }}
                >
                  <Add style={{ fontSize: '18px' }} />
                  {!isMobile && <span style={{ fontSize: '12px' }}>Create</span>}
                </Button>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            borderRadius: '10px',
            overflow: 'hidden'
          }}>
            <Table sx={{ minWidth: 650 }} aria-label="purchase table">
              <TableHead>
                <TableRow style={{
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                  height: '27px'
                }}>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Sl No.
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Bill No
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Bill Date
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Supplier
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Total Amount
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Balance Amount
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Good Receipt Status
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Status
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'center',
                    height: '27px'
                  }}>
                    PR Status
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'right',
                    height: '27px'
                  }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => (
                  <>
                    {/* Main Row - Simple View */}
                    <TableRow key={item.Id} style={{
                      background: !item.IsActive ? '#fef2f2' : (index % 2 === 0 ? '#ffffff' : '#f8f9fa'),
                      borderBottom: '2px solid #f0f0f0'
                    }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.IsActive ? (
                          <span onClick={() => handleOpenModal(item.IsVerified ? 'view' : 'edit', item)} style={{ color: '#1e40af', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}>
                            {startIndex + index + 1}
                          </span>
                        ) : (
                          <span style={{ fontWeight: 500, color: '#6b7280' }}>{startIndex + index + 1}</span>
                        )}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <ShoppingCart sx={{ fontSize: 18, color: item.IsActive ? '#0f3460' : '#6b7280' }} />
                          <span
                            onClick={() => togglePurchaseRowExpand(item.Id)}
                            style={{ color: '#1e40af', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}
                          >
                            {item.BillNumber || '-'}
                          </span>
                          <IconButton
                            size="small"
                            onClick={() => togglePurchaseRowExpand(item.Id)}
                            sx={{ p: 0.5, ml: 0.5 }}
                          >
                            {expandedPurchaseRowId === item.Id ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </div>
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(item.BillDate)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {/* {item.IsActive ? (
                          <span
                            onClick={() => handleSupplierClick(item.MasterId, getSupplierName(item))}
                            style={{ color: '#1e40af', cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}
                          >
                            {getSupplierName(item)}
                          </span>
                        ) : ( */}
                        <span style={{ color: '#6b7280' }}>{getSupplierName(item)}</span>
                        {/* )} */}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600 }}>{formatCurrency(item.TotalAmount, item.CurrencyName)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600 }}>{formatCurrency(item.BalanceAmount, item.CurrencyName)}</TableCell>

                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.IsConvertToGoodReceipt ? (
                          <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Received" color="success" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                        ) : (
                          <Chip icon={<Cancel sx={{ fontSize: 14 }} />} label="Pending" color="warning" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                        )}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.IsActive ? (
                          item.IsVerified ? (
                            <Chip label="Verified" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#8b5cf6 !important', color: '#ffffff !important' }} />
                          ) : item.IsConvertToGoodReceipt ? (
                            <Chip label="Good Receipt" color="success" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#10b981', color: 'white' }} />
                          ) : (
                            <Chip label="Active" color="success" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                          )
                        ) : (
                          <Chip label="Inactive" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#fee2e2', color: '#dc2626' }} />
                        )}
                      </TableCell>
                      {/* PR Status Column */}
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'center' }}>
                        {item.Status && item.Status !== '-' ? (
                          <Chip label={item.Status} size="small" sx={{ fontSize: '0.6rem', height: '18px', backgroundColor: '#fee2e2 !important', color: '#dc2626 !important', fontWeight: 600 }} />
                        ) : item.IsActive && item.IsVerified ? (
                          <Tooltip title="Create Purchase Return" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenPurchaseReturnModal(item)}
                              sx={{
                                bgcolor: '#f59e0b',
                                color: 'white',
                                width: 22,
                                height: 22,
                                '&:hover': { bgcolor: '#d97706' }
                              }}
                            >
                              <Assignment sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '0.7rem' }}>-</span>
                        )}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                          {item.IsActive ? (
                            <>
                              <Tooltip title="View Details" arrow>
                                <Button
                                  size="small"
                                  onClick={() => handleOpenModal('view', item)}
                                  style={{
                                    minWidth: '22px',
                                    height: '22px',
                                    borderRadius: '6px',
                                    color: 'white',
                                    padding: '2px',
                                    boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
                                    border: 'none',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                  }}
                                >
                                  👁️
                                </Button>
                              </Tooltip>

                              {canEditTransaction && !item.IsConvertToGoodReceipt && (
                                <Tooltip title="Edit Purchase" arrow>
                                  <Button
                                    size="small"
                                    onClick={() => handleOpenModal('edit', item)}
                                    style={{
                                      minWidth: '22px',
                                      height: '22px',
                                      borderRadius: '6px',
                                      color: 'white',
                                      padding: '2px',
                                      boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                                      border: 'none',
                                      fontSize: '10px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    ✏️
                                  </Button>
                                </Tooltip>
                              )}

                              {canEditTransaction && item.IsConvertToGoodReceipt && !item.IsVerified && (
                                <Tooltip title="Good Receipt" arrow>
                                  <Button
                                    size="small"
                                    onClick={() => handleOpenVerifyModal(item)}
                                    style={{
                                      minWidth: '22px',
                                      height: '22px',
                                      borderRadius: '6px',
                                      backgroundColor: '#10b981',
                                      color: 'white',
                                      padding: '2px',
                                      boxShadow: '0 1px 4px rgba(16, 185, 129, 0.3)',
                                      border: 'none',
                                      fontSize: '10px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    ✓
                                  </Button>
                                </Tooltip>
                              )}

                              {canDeleteTransaction && !item.IsVerified && (
                                <Tooltip title="Delete Purchase" arrow>
                                  <Button
                                    size="small"
                                    onClick={() => handleOpenDeleteModal(item.Id, item.BillNumber)}
                                    style={{
                                      minWidth: '22px',
                                      height: '22px',
                                      borderRadius: '6px',
                                      color: 'white',
                                      padding: '2px',
                                      boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                                      border: 'none',
                                      fontSize: '10px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    🗑️
                                  </Button>
                                </Tooltip>
                              )}
                            </>
                          ) : (
                            <>
                              <Tooltip title="View Details" arrow>
                                <Button
                                  size="small"
                                  onClick={() => handleOpenInactiveModal(item)}
                                  style={{
                                    minWidth: '22px',
                                    height: '22px',
                                    borderRadius: '6px',
                                    color: 'white',
                                    padding: '2px',
                                    boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
                                    border: 'none',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                  }}
                                >
                                  👁️
                                </Button>
                              </Tooltip>

                              {canEditTransaction && (
                                <Tooltip title="Activate Purchase" arrow>
                                  <Button
                                    size="small"
                                    onClick={() => handleOpenActivateModal(item.Id, item.BillNumber)}
                                    style={{
                                      minWidth: '22px',
                                      height: '22px',
                                      borderRadius: '6px',
                                      backgroundColor: '#10b981',
                                      color: 'white',
                                      padding: '2px',
                                      boxShadow: '0 1px 4px rgba(16, 185, 129, 0.3)',
                                      border: 'none',
                                      fontSize: '10px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    ✓
                                  </Button>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Animated Expanded Row - Enhanced View */}
                    {expandedPurchaseRowId === item.Id && (
                      <TableRow key={`${item.Id}-expanded`}>
                        <TableCell colSpan={10} style={{ padding: 0, backgroundColor: '#6b21a8', borderBottom: '1px solid #5b21b6' }}>
                          <Collapse in={expandedPurchaseRowId === item.Id} timeout={500}>
                            <Box sx={{ p: 0, bgcolor: '#5b21b6' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', margin: 0, tableLayout: 'fixed' }}>
                                <thead>
                                  <tr style={{ background: 'linear-gradient(135deg, #5b21b6 0%, #4c1d95 100%)' }}>
                                    <th style={{ background: 'linear-gradient(135deg, #5b21b6 0%, #4c1d95 100%)', padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#fde047', fontSize: '0.7rem', border: '1px solid #e9d5ff', width: '18%' }}>Purchase Info</th>
                                    <th style={{ background: 'linear-gradient(135deg, #5b21b6 0%, #4c1d95 100%)', padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#fde047', fontSize: '0.7rem', border: '1px solid #e9d5ff', width: '18%' }}>PO Reference</th>
                                    <th style={{ background: 'linear-gradient(135deg, #5b21b6 0%, #4c1d95 100%)', padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#fde047', fontSize: '0.7rem', border: '1px solid #e9d5ff', width: '16%' }}>Supplier</th>
                                    <th style={{ background: 'linear-gradient(135deg, #5b21b6 0%, #4c1d95 100%)', padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#fde047', fontSize: '0.7rem', border: '1px solid #e9d5ff', width: '16%' }}>Amounts</th>
                                    <th style={{ background: 'linear-gradient(135deg, #5b21b6 0%, #4c1d95 100%)', padding: '6px 8px', textAlign: 'center', fontWeight: 600, color: '#fde047', fontSize: '0.7rem', border: '1px solid #e9d5ff', width: '16%' }}>Good Receipt Status</th>
                                    <th style={{ background: 'linear-gradient(135deg, #5b21b6 0%, #4c1d95 100%)', padding: '6px 8px', textAlign: 'center', fontWeight: 600, color: '#fde047', fontSize: '0.7rem', border: '1px solid #e9d5ff', width: '16%' }}>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr style={{ background: 'white', animation: 'slideInFromTop 0.5s ease-out' }}>
                                    {/* Purchase Info */}
                                    <td style={{ padding: '6px 8px', verticalAlign: 'top', border: '1px solid #e9d5ff' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          <ShoppingCart sx={{ fontSize: 14, color: '#7c3aed' }} />
                                          <span style={{ fontWeight: 600, color: '#6b21a8', fontSize: '0.75rem' }}>{item.BillNumber || '-'}</span>
                                        </div>
                                        <div style={{ fontSize: '0.7rem', color: '#475569' }}>
                                          {formatDate(item.BillDate)}
                                        </div>
                                        {item.ClearingDate && (
                                          <div style={{ fontSize: '0.65rem', color: '#eab308' }}>
                                            Cleared: {formatDate(item.ClearingDate)}
                                          </div>
                                        )}
                                      </div>
                                    </td>

                                    {/* PO Reference */}
                                    <td style={{ padding: '6px 8px', verticalAlign: 'top', border: '1px solid #e9d5ff' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        {item.PurchaseOrderNo ? (
                                          <>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                              <Assignment sx={{ fontSize: 12, color: '#ca8a04' }} />
                                              <span
                                                style={{
                                                  fontWeight: 600,
                                                  color: '#ca8a04',
                                                  fontSize: '0.72rem',
                                                  cursor: 'pointer',
                                                  textDecoration: 'underline'
                                                }}
                                                onClick={() => handleOpenPODetailsModal({
                                                  Id: item.PurchaseOrderId,
                                                  PurchaseOrderNo: item.PurchaseOrderNo,
                                                  PurchaseOrderDate: item.PurchaseOrderDate
                                                })}
                                              >
                                                {item.PurchaseOrderNo}
                                              </span>
                                            </div>
                                            {item.PurchaseOrderDate && (
                                              <div style={{ fontSize: '0.65rem', color: '#475569' }}>
                                                {formatDate(item.PurchaseOrderDate)}
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div style={{ fontSize: '0.7rem', color: '#6b7280', fontStyle: 'italic' }}>
                                            Direct Purchase
                                          </div>
                                        )}
                                      </div>
                                    </td>

                                    {/* Supplier */}
                                    <td style={{ padding: '6px 8px', verticalAlign: 'top', border: '1px solid #e9d5ff' }}>
                                      <div style={{ fontWeight: 500, color: '#1e293b', fontSize: '0.75rem' }}>
                                        {getSupplierName(item)}
                                      </div>
                                    </td>

                                    {/* Amounts */}
                                    <td style={{ padding: '6px 8px', verticalAlign: 'top', border: '1px solid #e9d5ff' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <div style={{ fontWeight: 700, color: '#eab308', fontSize: '0.8rem' }}>
                                          {formatCurrency(item.TotalAmount, item.CurrencyName)}
                                        </div>
                                        {item.Amount !== item.TotalAmount && (
                                          <div style={{ fontSize: '0.65rem', color: '#475569' }}>
                                            Base: {formatCurrency(item.Amount, item.CurrencyName)}
                                          </div>
                                        )}
                                        {(item.VATAmount || item.IGSTAmount) > 0 && (
                                          <div style={{ fontSize: '0.65rem', color: '#7c3aed' }}>
                                            VAT: {formatCurrency((item.VATAmount || 0) + (item.IGSTAmount || 0), item.CurrencyName)}
                                          </div>
                                        )}
                                        {item.PaidAmount > 0 && (
                                          <div style={{ fontSize: '0.65rem', color: '#ca8a04' }}>
                                            Paid: {formatCurrency(item.PaidAmount, item.CurrencyName)}
                                          </div>
                                        )}
                                      </div>
                                    </td>

                                    {/* Receipt Status */}
                                    <td style={{ padding: '6px 8px', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #e9d5ff' }}>
                                      {item.IsConvertToGoodReceipt ? (
                                        <Chip
                                          icon={<CheckCircle sx={{ fontSize: 10 }} />}
                                          label="Received"
                                          size="small"
                                          sx={{ fontSize: '0.6rem', height: '18px', bgcolor: '#eab308', color: '#ffffff' }}
                                        />
                                      ) : (
                                        <Chip
                                          icon={<Cancel sx={{ fontSize: 10 }} />}
                                          label="Pending"
                                          color="warning"
                                          size="small"
                                          sx={{ fontSize: '0.6rem', height: '18px' }}
                                        />
                                      )}
                                    </td>

                                    {/* Status */}
                                    <td style={{ padding: '6px 8px', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #e9d5ff' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
                                        {item.IsActive ? (
                                          item.IsVerified ? (
                                            <Chip
                                              label="Verified"
                                              size="small"
                                              sx={{ fontSize: '0.6rem', height: '18px', bgcolor: '#7c3aed !important', color: '#ffffff !important' }}
                                            />
                                          ) : item.IsConvertToGoodReceipt ? (
                                            <Chip
                                              label="Good Receipt"
                                              size="small"
                                              sx={{ fontSize: '0.6rem', height: '18px', bgcolor: '#eab308', color: 'white' }}
                                            />
                                          ) : (
                                            <Chip
                                              label="Active"
                                              color="success"
                                              size="small"
                                              sx={{ fontSize: '0.6rem', height: '18px' }}
                                            />
                                          )
                                        ) : (
                                          <Chip
                                            label="Inactive"
                                            size="small"
                                            sx={{ fontSize: '0.6rem', height: '18px', bgcolor: '#fee2e2', color: '#dc2626' }}
                                          />
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
                {paginatedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                      No purchases found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div >

        {/* Pagination Section - Mobile Optimized */}
        {
          totalCount > 0 && (
            <div style={{ marginTop: '10px' }}>
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                padding: isMobile ? '8px' : '8px 12px',
                background: '#f8f9fa',
                borderRadius: '8px',
                gap: isMobile ? '8px' : '0'
              }}>
                {/* Info and Rows per page - Combined on mobile */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ fontSize: isMobile ? '11px' : '13px', color: '#64748b', whiteSpace: 'nowrap' }}>
                    {isMobile ? `${startIndex + 1}-${Math.min(startIndex + rowsPerPage, totalCount)} of ${totalCount}` : `Showing ${totalCount > 0 ? startIndex + 1 : 0} to ${Math.min(startIndex + rowsPerPage, totalCount)} of ${totalCount} entries`}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <span style={{ fontSize: isMobile ? '11px' : '13px', color: '#64748b' }}>Rows:</span>
                    <select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                      style={{
                        padding: isMobile ? '3px 6px' : '4px 8px',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                        fontSize: isMobile ? '11px' : '13px',
                        cursor: 'pointer',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>

                {/* Pagination Controls */}
                <div style={{
                  display: 'flex',
                  gap: isMobile ? '3px' : '4px',
                  justifyContent: isMobile ? 'center' : 'flex-end',
                  flexWrap: 'wrap'
                }}>
                  <Button
                    size="small"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    sx={{
                      minWidth: isMobile ? '32px' : '60px',
                      fontSize: isMobile ? '11px' : '12px',
                      textTransform: 'none',
                      bgcolor: currentPage === 1 ? '#f1f5f9' : 'white',
                      color: currentPage === 1 ? '#94a3b8' : '#475569',
                      border: '1px solid #e2e8f0',
                      padding: isMobile ? '4px 6px' : '4px 12px',
                      '&:hover': { bgcolor: '#f8fafc' }
                    }}
                  >
                    {isMobile ? '«' : 'First'}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    sx={{
                      minWidth: isMobile ? '32px' : '60px',
                      fontSize: isMobile ? '11px' : '12px',
                      textTransform: 'none',
                      bgcolor: currentPage === 1 ? '#f1f5f9' : 'white',
                      color: currentPage === 1 ? '#94a3b8' : '#475569',
                      border: '1px solid #e2e8f0',
                      padding: isMobile ? '4px 6px' : '4px 12px',
                      '&:hover': { bgcolor: '#f8fafc' }
                    }}
                  >
                    {isMobile ? '‹' : 'Prev'}
                  </Button>
                  {[...Array(totalPages)].map((_, i) => {
                    const page = i + 1;
                    // On mobile, show fewer page numbers
                    if (isMobile) {
                      if (page === currentPage || page === 1 || page === totalPages) {
                        return (
                          <Button
                            key={page}
                            size="small"
                            onClick={() => handlePageChange(page)}
                            sx={{
                              minWidth: '32px',
                              fontSize: '11px',
                              bgcolor: currentPage === page ? '#0f3460' : 'white',
                              color: currentPage === page ? 'white' : '#475569',
                              border: '1px solid #e2e8f0',
                              padding: '4px 6px',
                              fontWeight: currentPage === page ? 600 : 400,
                              '&:hover': { bgcolor: currentPage === page ? '#5568d3' : '#f8fafc' }
                            }}
                          >
                            {page}
                          </Button>
                        );
                      } else if (page === currentPage - 1 || page === currentPage + 1) {
                        return <span key={page} style={{ padding: '0 2px', color: '#94a3b8', fontSize: '11px' }}>...</span>;
                      }
                    } else {
                      // Desktop view - show more pages
                      if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                        return (
                          <Button
                            key={page}
                            size="small"
                            onClick={() => handlePageChange(page)}
                            sx={{
                              minWidth: '36px',
                              fontSize: '12px',
                              bgcolor: currentPage === page ? '#0f3460' : 'white',
                              color: currentPage === page ? 'white' : '#475569',
                              border: '1px solid #e2e8f0',
                              '&:hover': { bgcolor: currentPage === page ? '#5568d3' : '#f8fafc' }
                            }}
                          >
                            {page}
                          </Button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>;
                      }
                    }
                    return null;
                  })}
                  <Button
                    size="small"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || totalPages === 0}
                    sx={{
                      minWidth: isMobile ? '32px' : '60px',
                      fontSize: isMobile ? '11px' : '12px',
                      textTransform: 'none',
                      bgcolor: (currentPage === totalPages || totalPages === 0) ? '#f1f5f9' : 'white',
                      color: (currentPage === totalPages || totalPages === 0) ? '#94a3b8' : '#475569',
                      border: '1px solid #e2e8f0',
                      padding: isMobile ? '4px 6px' : '4px 12px',
                      '&:hover': { bgcolor: '#f8fafc' }
                    }}
                  >
                    {isMobile ? '›' : 'Next'}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages || totalPages === 0}
                    sx={{
                      minWidth: isMobile ? '32px' : '60px',
                      fontSize: isMobile ? '11px' : '12px',
                      textTransform: 'none',
                      bgcolor: (currentPage === totalPages || totalPages === 0) ? '#f1f5f9' : 'white',
                      color: (currentPage === totalPages || totalPages === 0) ? '#94a3b8' : '#475569',
                      border: '1px solid #e2e8f0',
                      padding: isMobile ? '4px 6px' : '4px 12px',
                      '&:hover': { bgcolor: '#f8fafc' }
                    }}
                  >
                    {isMobile ? '»' : 'Last'}
                  </Button>
                </div>
              </div>
            </div>
          )
        }
      </div >

      {/* Purchase Modal */}
      < PurchaseModal
        open={openModal}
        onClose={handleCloseModal}
        modalAction={modalAction}
        formData={formData}
        setFormData={setFormData}
        purchaseDetails={purchaseDetails}
        setPurchaseDetails={setPurchaseDetails}
        onSave={handleSave}
        onVerify={handleVerifyFromModal}
        onToGoodReceipt={handleOpenConvertModal}
        suppliers={suppliers}
        items={items}
        loading={loading}
        freightPackingGST={freightPackingGST}
        companyId={companyId}
        token={token}
        setLoading={setLoading}
        setLoadingMessage={setLoadingMessage}
      />

      {/* Delete Confirmation Modal */}
      < Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal} maxWidth="xs" fullWidth >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#dc2626' }}>
          <Warning /> Delete Purchase
        </DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete purchase <strong>{deleteItemNo}</strong>?</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog >

      {/* Activate Confirmation Modal */}
      < Dialog open={activateModalOpen} onClose={handleCloseActivateModal} maxWidth="xs" fullWidth >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#10b981' }}>
          <CheckCircle /> Activate Purchase
        </DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to activate purchase <strong>{activateItemNo}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActivateModal}>Cancel</Button>
          <Button onClick={handleActivate} color="success" variant="contained">Activate</Button>
        </DialogActions>
      </Dialog >

      {/* Convert to Good Receipt Confirmation Modal */}
      < Dialog open={convertModalOpen} onClose={handleCloseConvertModal} maxWidth="xs" fullWidth >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#8b5cf6' }}>
          <Assignment /> Convert to Good Receipt
        </DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to convert purchase <strong>{convertItemNo}</strong> to Good Receipt?</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>This action will enable the receipt process for this purchase.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConvertModal}>Cancel</Button>
          <Button onClick={handleToGoodReceipt} variant="contained" sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}>Convert</Button>
        </DialogActions>
      </Dialog >

      {/* Inactive Purchase View Modal */}
      < Dialog
        open={inactiveModalOpen}
        onClose={handleCloseInactiveModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '85vh', maxWidth: '1200px' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#dc2626', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                View Inactive Purchase
              </Typography>
              <Chip label="Inactive" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#fee2e2', color: '#dc2626' }} />
            </Box>
            <IconButton onClick={handleCloseInactiveModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {inactiveViewData && (
            <>
              {/* Purchase Information */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626', mb: 1, display: 'block' }}>Purchase Information</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, p: 1.5, bgcolor: '#f9fafb', borderRadius: 1 }}>
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Bill Number</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{inactiveViewData.BillNumber || '-'}</Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(inactiveViewData.BillDate)}</Typography>
                </Box>
                <Box sx={{ minWidth: 150 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Supplier</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{inactiveViewData.MasterName || getSupplierName(inactiveViewData)}</Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Total Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e40af' }}>{formatCurrency(inactiveViewData.TotalAmount, inactiveViewData.CurrencyName)}</Typography>
                </Box>
              </Box>

              {/* Purchase Items */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626', mb: 1, display: 'block' }}>Purchase Items ({inactiveViewDetails.length} items)</Typography>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
                <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: '#fee2e2' }}>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#991b1b' }}>#</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#991b1b' }}>Item</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Rcvd Qty</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Dmg Qty</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Stock Qty</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Rate</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Disc%</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Disc Amt</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#991b1b' }}>VAT%</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Taxable</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inactiveViewDetails.map((detail, index) => (
                        <tr key={detail.Id || index} style={{ borderBottom: '1px solid #e2e8f0', background: index % 2 === 0 ? '#fff' : '#f9fafb' }}>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{index + 1}</td>
                          <td style={{ padding: '8px', fontWeight: 500 }}>{detail.ItemName || items.find(i => i.Id === detail.ItemId)?.Name || '-'}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{detail.ReceivedQuantity || 0}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{detail.DamageQuantity || 0}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{detail.StockQuantity || 0}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(detail.Rate, inactiveViewData.CurrencyName)}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{detail.DiscountPercentage || 0}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{detail.DiscountAmount || 0}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{detail.VATRate || 0}%</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(detail.TaxableAmount, inactiveViewData.CurrencyName)}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>{formatCurrency(detail.TotalAmount, inactiveViewData.CurrencyName)}</td>
                        </tr>
                      ))}
                      {inactiveViewDetails.length === 0 && (
                        <tr>
                          <td colSpan="11" style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>No items found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Purchase Summary */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626', mb: 1, display: 'block' }}>Purchase Summary</Typography>
              <Box sx={{ bgcolor: '#fef2f2', borderRadius: 1, p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Sub Total:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(inactiveViewData.Amount, inactiveViewData.CurrencyName)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Discount:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: '#dc2626' }}>- {formatCurrency(inactiveViewData.DiscountAmount, inactiveViewData.CurrencyName)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Taxable:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(inactiveViewData.TaxableAmount, inactiveViewData.CurrencyName)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">VAT:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: '#166534' }}>{formatCurrency((inactiveViewData.VATAmount || 0) + (inactiveViewData.IGSTAmount || 0), inactiveViewData.CurrencyName)}</Typography>
                </Box>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626' }}>Grand Total:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.9rem' }}>{formatCurrency(inactiveViewData.TotalAmount, inactiveViewData.CurrencyName)}</Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#fef2f2' }}>
          <Button onClick={handleCloseInactiveModal} variant="outlined" size="small" sx={{ borderColor: '#dc2626', color: '#dc2626', '&:hover': { borderColor: '#b91c1c', bgcolor: '#fee2e2' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog >

      {/* Converted PO List Modal */}
      < Dialog
        open={convertedPOModalOpen}
        onClose={handleCloseConvertedPOModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f5f3ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment sx={{ color: '#7c3aed', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Converted Purchase Orders
              </Typography>
              <Chip label={`${convertedPOList.length} POs`} size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#ede9fe', color: '#7c3aed' }} />
            </Box>
            <IconButton onClick={handleCloseConvertedPOModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: '#ede9fe' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#5b21b6' }}>#</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#5b21b6' }}>PO Number</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#5b21b6' }}>PO Date</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#5b21b6' }}>Supplier</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#5b21b6' }}>Total Amount</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#5b21b6' }}>Status</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#5b21b6' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {convertedPOList.map((po, index) => (
                    <tr key={po.Id || index} style={{ borderBottom: '1px solid #e2e8f0', background: index % 2 === 0 ? '#fff' : '#faf5ff' }}>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>{index + 1}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: '#7c3aed' }}>{po.PurchaseOrderNo || '-'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>{formatDate(po.PurchaseOrderDate)}</td>
                      <td style={{ padding: '10px 12px' }}>{po.MasterName || '-'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(po.TotalAmount, po.CurrencyName)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <Chip
                          label={po.IsConverted ? 'Converted' : 'Pending'}
                          size="small"
                          sx={{
                            fontSize: '0.65rem',
                            height: '20px',
                            bgcolor: po.IsConverted ? '#d1fae5' : '#fef3c7',
                            color: po.IsConverted ? '#065f46' : '#92400e'
                          }}
                        />
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="To Purchase">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleOpenPOToPurchaseModal(po)}
                              sx={{
                                fontSize: '0.65rem',
                                py: 0.25,
                                px: 1,
                                minWidth: 'auto',
                                bgcolor: '#10b981',
                                '&:hover': { bgcolor: '#059669' }
                              }}
                            >
                              To Purchase
                            </Button>
                          </Tooltip>
                        </Box>
                      </td>
                    </tr>
                  ))}
                  {convertedPOList.length === 0 && (
                    <tr>
                      <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                        <Assignment sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                        <Typography variant="body2" color="textSecondary">No converted purchase orders found</Typography>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f5f3ff' }}>
          <Button onClick={handleCloseConvertedPOModal} variant="outlined" size="small" sx={{ borderColor: '#7c3aed', color: '#7c3aed', '&:hover': { borderColor: '#6d28d9', bgcolor: '#ede9fe' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog >

      {/* PO to Purchase Create Modal */}
      < Dialog
        open={poToPurchaseModalOpen}
        onClose={handleClosePOToPurchaseModal}
        maxWidth={false}
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', maxHeight: isMobile ? '100vh' : '95vh', width: isMobile ? '100vw' : '95vw', maxWidth: isMobile ? '100vw' : '1500px', m: isMobile ? 0 : 1 } }}
      >
        <DialogTitle sx={{ py: isMobile ? 1.5 : 1, px: isMobile ? 1.5 : 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f0fdf4', position: isMobile ? 'sticky' : 'relative', top: 0, zIndex: 10 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#10b981', fontSize: isMobile ? 18 : 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', fontSize: isMobile ? '0.85rem' : '0.95rem' }}>
                {isMobile ? 'Purchase from PO' : 'Create Purchase from PO'}
              </Typography>
              {selectedPOData && (
                <Chip
                  label={selectedPOData.PurchaseOrderNo}
                  size="small"
                  onClick={() => handleOpenPODetailsModal(selectedPOData)}
                  sx={{
                    fontSize: '0.65rem',
                    height: '20px',
                    bgcolor: '#d1fae5',
                    color: '#065f46',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#a7f3d0' }
                  }}
                />
              )}
            </Box>
            <IconButton onClick={handleClosePOToPurchaseModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 1.5 : 1.5, pb: isMobile ? '70px' : 1.5 }}>
          {selectedPOData && (
            <>
              {/* Purchase Information Section */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981', mb: 0.5, display: 'block', fontSize: '0.7rem' }}>Purchase Information</Typography>

              {isMobile ? (
                /* MOBILE: Compact card-style form */
                <Box sx={{ mb: 1, bgcolor: '#f9fafb', borderRadius: '10px', p: 1.25, border: '1px solid #f1f5f9' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mb: 0.75 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25, fontSize: '0.6rem', fontWeight: 500 }}>Bill No *</Typography>
                      <input
                        type="text"
                        value={poToPurchaseFormData.BillNumber || ''}
                        onChange={(e) => handlePOToPurchaseFormChange('BillNumber', e.target.value)}
                        style={{
                          width: '100%', padding: '7px 10px', border: `1px solid ${!poToPurchaseFormData.BillNumber?.trim() ? '#ef4444' : '#e2e8f0'}`,
                          borderRadius: '6px', fontSize: '13px', backgroundColor: !poToPurchaseFormData.BillNumber?.trim() ? '#fef2f2' : '#fff',
                          outline: 'none'
                        }}
                        placeholder="Enter Bill No"
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25, fontSize: '0.6rem', fontWeight: 500 }}>Bill Date *</Typography>
                      <input
                        type="date"
                        value={poToPurchaseFormData.BillDate || ''}
                        onChange={(e) => handlePOToPurchaseFormChange('BillDate', e.target.value)}
                        style={{
                          width: '100%', padding: '7px 10px', border: `1px solid ${!poToPurchaseFormData.BillDate ? '#ef4444' : '#e2e8f0'}`,
                          borderRadius: '6px', fontSize: '13px', backgroundColor: !poToPurchaseFormData.BillDate ? '#fef2f2' : '#fff'
                        }}
                      />
                    </Box>
                  </Box>
                  {/* Supplier - full width */}
                  <Box sx={{ mb: 0.75 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25, fontSize: '0.6rem', fontWeight: 500 }}>Supplier</Typography>
                    <Box sx={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', bgcolor: '#fff', fontSize: '13px', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedPOData.MasterName || '-'}
                    </Box>
                  </Box>
                  {/* VAT Number + Currency */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mb: 0.75 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25, fontSize: '0.6rem', fontWeight: 500 }}>VAT Number</Typography>
                      <Box sx={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', bgcolor: '#fff', fontSize: '13px', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {suppliers.find(s => (s.MasterId || s.Id) === selectedPOData.MasterId)?.VATNumber || suppliers.find(s => (s.MasterId || s.Id) === selectedPOData.MasterId)?.GSTNumber || '-'}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25, fontSize: '0.6rem', fontWeight: 500 }}>Currency</Typography>
                      <Box sx={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', bgcolor: '#e0f7fa', fontSize: '12px', fontWeight: 700, color: '#006064', textAlign: 'center' }}>
                        {poToPurchaseFormData.CurrencyName || '-'}
                      </Box>
                    </Box>
                  </Box>
                  {/* VAT Type + Clearing Date */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25, fontSize: '0.6rem', fontWeight: 500 }}>VAT Type *</Typography>
                      <select
                        value={poToPurchaseFormData.VATCalculationType || 1}
                        onChange={(e) => {
                          const vatType = parseInt(e.target.value);
                          const vatTypeName = vatType === 1 ? 'Excluding Tax' : vatType === 2 ? 'Including Tax' : 'Tax Free';
                          handlePOToPurchaseFormChange('VATCalculationType', vatType);
                          handlePOToPurchaseFormChange('VATCalculationTypeName', vatTypeName);
                        }}
                        style={{ width: '100%', padding: '7px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', backgroundColor: '#fff', fontWeight: 500 }}
                      >
                        <option value={1}>Excluding Tax</option>
                        <option value={2}>Including Tax</option>
                        <option value={3}>Tax Free</option>
                      </select>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25, fontSize: '0.6rem', fontWeight: 500 }}>Clearing Date</Typography>
                      <input
                        type="date"
                        value={poToPurchaseFormData.ClearingDate || new Date().toISOString().split('T')[0]}
                        onChange={(e) => handlePOToPurchaseFormChange('ClearingDate', e.target.value)}
                        style={{ width: '100%', padding: '7px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', backgroundColor: '#fff' }}
                      />
                    </Box>
                  </Box>
                </Box>
              ) : (
                /* DESKTOP: Original grid layout */
                <Grid container spacing={0.5} sx={{ mb: 1 }}>
                  <Grid item xs={6} sm={1.5}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25, fontSize: '0.65rem' }}>Bill No *</Typography>
                    <input
                      type="text"
                      value={poToPurchaseFormData.BillNumber || ''}
                      onChange={(e) => handlePOToPurchaseFormChange('BillNumber', e.target.value)}
                      style={{
                        width: '100%', padding: '4px 6px',
                        border: `1px solid ${!poToPurchaseFormData.BillNumber?.trim() ? '#ef4444' : '#d1d5db'}`,
                        borderRadius: '4px', fontSize: '11px',
                        backgroundColor: !poToPurchaseFormData.BillNumber?.trim() ? '#fef2f2' : 'white'
                      }}
                      placeholder="Enter Bill No"
                      required
                    />
                  </Grid>
                  <Grid item xs={6} sm={1.3}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25, fontSize: '0.65rem' }}>Bill Date *</Typography>
                    <input
                      type="date"
                      value={poToPurchaseFormData.BillDate || ''}
                      onChange={(e) => handlePOToPurchaseFormChange('BillDate', e.target.value)}
                      style={{
                        width: '100%', padding: '4px 6px',
                        border: `1px solid ${!poToPurchaseFormData.BillDate ? '#ef4444' : '#d1d5db'}`,
                        borderRadius: '4px', fontSize: '11px',
                        backgroundColor: !poToPurchaseFormData.BillDate ? '#fef2f2' : 'white'
                      }}
                      required
                    />
                  </Grid>
                  <Grid item xs={6} sm={2.5}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25, fontSize: '0.65rem' }}>Supplier *</Typography>
                    <input
                      type="text"
                      value={selectedPOData.MasterName || ''}
                      disabled
                      style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', backgroundColor: '#f9fafb', color: '#5D4037', fontWeight: 700 }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25, fontSize: '0.65rem' }}>VAT Number</Typography>
                    <input
                      type="text"
                      value={suppliers.find(s => (s.MasterId || s.Id) === selectedPOData.MasterId)?.VATNumber || suppliers.find(s => (s.MasterId || s.Id) === selectedPOData.MasterId)?.GSTNumber || suppliers.find(s => (s.MasterId || s.Id) === selectedPOData.MasterId)?.GSTNo || '-'}
                      disabled
                      style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', backgroundColor: '#f0fdf4', color: '#5D4037', fontWeight: 700 }}
                    />
                  </Grid>
                  {/* <Grid item xs={6} sm={1}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25, fontSize: '0.65rem' }}>Inv Type</Typography>
                    <input
                      type="text"
                      value={suppliers.find(s => (s.MasterId || s.Id) === selectedPOData.MasterId)?.InvoiceTypeName || suppliers.find(s => (s.MasterId || s.Id) === selectedPOData.MasterId)?.InvoiceType || '-'}
                      disabled
                      style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', backgroundColor: '#fef3c7', color: '#5D4037', fontWeight: 700 }}
                    />
                  </Grid> */}
                  <Grid item xs={6} sm={1}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25, fontSize: '0.65rem' }}>Currency</Typography>
                    <input
                      type="text"
                      value={poToPurchaseFormData.CurrencyName || '-'}
                      disabled
                      style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', backgroundColor: '#e0f7fa', color: '#5D4037', fontWeight: 700 }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={1.5}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25, fontSize: '0.65rem' }}>VAT Type *</Typography>
                    <select
                      value={poToPurchaseFormData.VATCalculationType || 1}
                      onChange={(e) => {
                        const vatType = parseInt(e.target.value);
                        const vatTypeName = vatType === 1 ? 'Excluding Tax' : vatType === 2 ? 'Including Tax' : 'Tax Free';
                        handlePOToPurchaseFormChange('VATCalculationType', vatType);
                        handlePOToPurchaseFormChange('VATCalculationTypeName', vatTypeName);
                      }}
                      style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', backgroundColor: '#e0f2fe' }}
                    >
                      <option value={1}>Excluding Tax</option>
                      <option value={2}>Including Tax</option>
                      <option value={3}>Tax Free</option>
                    </select>
                  </Grid>
                  <Grid item xs={6} sm={1.2}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25, fontSize: '0.65rem' }}>Clearing Date</Typography>
                    <input
                      type="date"
                      value={poToPurchaseFormData.ClearingDate || new Date().toISOString().split('T')[0]}
                      onChange={(e) => handlePOToPurchaseFormChange('ClearingDate', e.target.value)}
                      style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Items Table */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981', fontSize: '0.7rem' }}>Items ({selectedPODetails.length} items)</Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => {
                    const newItem = {
                      Id: 0,
                      ItemId: '',
                      ItemName: '',
                      HSNCode: '',
                      Quantity: 0,
                      ReceivedQuantity: 0,
                      Rate: 0,
                      Amount: 0,
                      DiscountPercentage: 0,
                      DiscountAmount: 0,
                      VATRate: 0,
                      TaxableAmount: 0,
                      VATAmount: 0,
                      IGSTAmount: 0,
                      TotalAmount: 0,
                      DamageQuantity: 0,
                      StockQuantity: 0
                    };
                    const updatedDetails = [...selectedPODetails, newItem];
                    setSelectedPODetails(updatedDetails);
                    calculatePOTotals(updatedDetails);
                  }}
                  sx={{
                    bgcolor: '#10b981',
                    color: 'white',
                    fontSize: '0.7rem',
                    height: '24px',
                    '&:hover': { bgcolor: '#059669' }
                  }}
                >
                  Add Item
                </Button>
              </Box>

              {isMobile ? (
                /* MOBILE: Card-based items list */
                <Box sx={{ mb: 1 }}>
                  {selectedPODetails.map((detail, index) => (
                    <Box key={detail.Id || index} sx={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      mb: 0.75,
                      overflow: 'hidden',
                      bgcolor: (!detail.ItemId || !detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0 || !detail.Rate || parseFloat(detail.Rate) <= 0) ? '#fef2f2' : '#fff',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                    }}>
                      {/* Item header with number and delete */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25, py: 0.5, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.65rem' }}>#{index + 1}</Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const updatedDetails = selectedPODetails.filter((_, i) => i !== index);
                            setSelectedPODetails(updatedDetails);
                            calculatePOTotals(updatedDetails);
                          }}
                          sx={{ color: '#ef4444', p: 0.25 }}
                        >
                          <Delete sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                      {/* Item select */}
                      <Box sx={{ px: 1.25, pt: 0.75, pb: 0.5 }}>
                        <select
                          value={detail.ItemId || ''}
                          onChange={(e) => {
                            const itemId = e.target.value;
                            if (itemId) {
                              const selectedItem = items.find(item => item.Id === parseInt(itemId));
                              if (selectedItem) {
                                const updatedDetails = [...selectedPODetails];
                                const itemRate = selectedItem.PurchaseRate || selectedItem.Rate || selectedItem.SaleRate || selectedItem.MRP || 0;
                                const vatRate = selectedItem.VATRate || selectedItem.GST || 0;
                                const quantity = 1;
                                const isIncludingTax = (poToPurchaseFormData.VATCalculationType || 1) === 2;
                                let amount, taxableAmount, vatAmount;
                                if (isIncludingTax) {
                                  const totalWithGst = quantity * itemRate;
                                  taxableAmount = totalWithGst / (1 + vatRate / 100);
                                  amount = taxableAmount;
                                  vatAmount = (taxableAmount * vatRate) / 100;
                                } else {
                                  amount = quantity * itemRate;
                                  taxableAmount = amount;
                                  vatAmount = (taxableAmount * vatRate) / 100;
                                }
                                updatedDetails[index] = {
                                  ...updatedDetails[index],
                                  ItemId: parseInt(itemId), ItemName: selectedItem.Name || '',
                                  HSNCode: selectedItem.HSNCode || selectedItem.HSN || '',
                                  VATRate: vatRate, Rate: itemRate, Quantity: quantity,
                                  ReceivedQuantity: quantity, StockQuantity: quantity, DamageQuantity: 0,
                                  DiscountPercentage: 0, DiscountAmount: 0,
                                  Amount: Math.round(amount * 100) / 100,
                                  TaxableAmount: Math.round(taxableAmount * 100) / 100,
                                  VATAmount: vatAmount, IGSTAmount: 0,
                                  TotalAmount: Math.round((taxableAmount + vatAmount) * 100) / 100
                                };
                                setSelectedPODetails(updatedDetails);
                                calculatePOTotals(updatedDetails);
                              }
                            } else {
                              handlePOToPurchaseDetailChange(index, 'ItemId', '');
                            }
                          }}
                          style={{
                            width: '100%', padding: '8px 10px', border: `1px solid ${!detail.ItemId ? '#ef4444' : '#e2e8f0'}`,
                            borderRadius: '6px', fontSize: '13px', backgroundColor: !detail.ItemId ? '#fef2f2' : '#fff', fontWeight: 500
                          }}
                        >
                          <option value="">Select Item</option>
                          {items.map(item => (
                            <option key={item.Id} value={item.Id}>{item.Name}</option>
                          ))}
                        </select>
                      </Box>
                      {/* Compact row: HSN, Qty, Rate, Disc, VAT */}
                      <Box sx={{ display: 'grid', gridTemplateColumns: '0.7fr 1fr 1fr 1fr 0.7fr', gap: 0.5, px: 1.25, py: 0.5 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.5rem', display: 'block' }}>HSN</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>{detail.HSNCode || '-'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.5rem', display: 'block' }}>Ord Qty</Typography>
                          <input
                            type="number"
                            value={detail.Quantity || ''}
                            onChange={(e) => handlePOToPurchaseDetailChange(index, 'Quantity', e.target.value)}
                            style={{ width: '100%', padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: '5px', textAlign: 'center', fontSize: '12px', fontWeight: 600 }}
                            min="0"
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.5rem', display: 'block' }}>Rcvd</Typography>
                          <input
                            type="number"
                            value={detail.ReceivedQuantity || ''}
                            onChange={(e) => handlePOToPurchaseDetailChange(index, 'ReceivedQuantity', e.target.value)}
                            style={{
                              width: '100%', padding: '4px 6px',
                              border: `1px solid ${!detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0 ? '#ef4444' : '#e2e8f0'}`,
                              borderRadius: '5px', textAlign: 'center', fontSize: '12px', fontWeight: 600,
                              backgroundColor: (!detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0) ? '#fef2f2' : '#fff'
                            }}
                            min="0" step="0.01"
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.5rem', display: 'block' }}>Rate</Typography>
                          <input
                            type="number"
                            value={detail.Rate || ''}
                            onChange={(e) => handlePOToPurchaseDetailChange(index, 'Rate', e.target.value)}
                            style={{
                              width: '100%', padding: '4px 6px',
                              border: `1px solid ${!detail.Rate || parseFloat(detail.Rate) <= 0 ? '#ef4444' : '#e2e8f0'}`,
                              borderRadius: '5px', textAlign: 'right', fontSize: '12px', fontWeight: 600,
                              backgroundColor: (!detail.Rate || parseFloat(detail.Rate) <= 0) ? '#fef2f2' : '#fff'
                            }}
                            min="0" step="0.01"
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.5rem', display: 'block' }}>VAT%</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#059669', fontSize: '0.7rem', display: 'block', pt: 0.25, textAlign: 'center' }}>{detail.VATRate || 0}%</Typography>
                        </Box>
                      </Box>
                      {/* Totals row */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1.25, py: 0.75, bgcolor: '#f0fdf4', borderTop: '1px solid #e2e8f0' }}>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.5rem', display: 'block' }}>Taxable</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#059669', fontSize: '0.7rem' }}>{formatCurrency(detail.TaxableAmount || 0)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.5rem', display: 'block' }}>VAT</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626', fontSize: '0.7rem' }}>{formatCurrency((detail.VATAmount || 0) + (detail.IGSTAmount || 0))}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.5rem', display: 'block' }}>Total</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#10b981', fontSize: '0.85rem' }}>{formatCurrency(detail.TotalAmount || 0)}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                  {selectedPODetails.length === 0 && (
                    <Box sx={{ p: 2.5, textAlign: 'center', color: '#6b7280', border: '1px dashed #e2e8f0', borderRadius: '10px' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>No items found</Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                /* DESKTOP: Original table */
                <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 1 }}>
                  <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr style={{ background: '#d1fae5' }}>
                          <th style={{ padding: '5px 4px', textAlign: 'center', fontWeight: 600, color: '#065f46', fontSize: '0.65rem' }}>#</th>
                          <th style={{ padding: '5px 4px', textAlign: 'left', fontWeight: 600, color: '#065f46', fontSize: '0.65rem' }}>Item</th>
                          <th style={{ padding: '5px 4px', textAlign: 'center', fontWeight: 600, color: '#065f46', fontSize: '0.65rem' }}>HSN</th>
                          <th style={{ padding: '5px 4px', textAlign: 'center', fontWeight: 600, color: '#065f46', fontSize: '0.65rem' }}>Order Qty</th>
                          <th style={{ padding: '5px 4px', textAlign: 'center', fontWeight: 600, color: '#065f46', fontSize: '0.65rem' }}>Rcvd Qty</th>
                          <th style={{ padding: '5px 4px', textAlign: 'center', fontWeight: 600, color: '#065f46', fontSize: '0.65rem' }}>Disc%</th>
                          <th style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 600, color: '#065f46', fontSize: '0.65rem' }}>DiscAmt</th>
                          <th style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 600, color: '#065f46', fontSize: '0.65rem' }}>Rate</th>
                          <th style={{ padding: '5px 4px', textAlign: 'center', fontWeight: 600, color: '#065f46', fontSize: '0.65rem' }}>VAT%</th>
                          <th style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 600, color: '#065f46', fontSize: '0.65rem' }}>Taxable</th>
                          <th style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 600, color: '#065f46', fontSize: '0.65rem' }}>VAT Amt</th>
                          <th style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 600, color: '#065f46', fontSize: '0.65rem' }}>Total</th>
                          <th style={{ padding: '5px 4px', textAlign: 'center', fontWeight: 600, color: '#065f46', fontSize: '0.65rem' }}>Act</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPODetails.map((detail, index) => (
                          <tr key={detail.Id || index} style={{
                            borderBottom: '1px solid #e2e8f0',
                            background: index % 2 === 0 ? '#fff' : '#f0fdf4',
                            backgroundColor: (!detail.ItemId || !detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0 || !detail.Rate || parseFloat(detail.Rate) <= 0) ? '#fef2f2' : (index % 2 === 0 ? '#fff' : '#f0fdf4')
                          }}>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{index + 1}</td>
                            <td style={{ padding: '8px', fontWeight: 500 }}>
                              <select
                                value={detail.ItemId || ''}
                                onChange={(e) => {
                                  const itemId = e.target.value;
                                  console.log('Item selected:', itemId, 'for index:', index);

                                  if (itemId) {
                                    const selectedItem = items.find(item => item.Id === parseInt(itemId));
                                    console.log('Selected item details:', selectedItem);
                                    console.log('Available item fields:', Object.keys(selectedItem || {}));

                                    if (selectedItem) {
                                      // Update all fields in sequence
                                      const updatedDetails = [...selectedPODetails];
                                      const itemRate = selectedItem.PurchaseRate || selectedItem.Rate || selectedItem.SaleRate || selectedItem.MRP || 0;
                                      const vatRate = selectedItem.VATRate || selectedItem.GST || 0;
                                      const quantity = 1;

                                      // Calculate amounts based on GST type
                                      const isIncludingTax = (poToPurchaseFormData.VATCalculationType || 1) === 2;
                                      let amount, taxableAmount, vatAmount;

                                      if (isIncludingTax) {
                                        // Rate includes GST
                                        const totalWithGst = quantity * itemRate;
                                        taxableAmount = totalWithGst / (1 + vatRate / 100);
                                        amount = taxableAmount;
                                        vatAmount = (taxableAmount * vatRate) / 100;
                                      } else {
                                        // Rate excludes GST
                                        amount = quantity * itemRate;
                                        taxableAmount = amount;
                                        vatAmount = (taxableAmount * vatRate) / 100;
                                      }

                                      updatedDetails[index] = {
                                        ...updatedDetails[index],
                                        ItemId: parseInt(itemId),
                                        ItemName: selectedItem.Name || '',
                                        HSNCode: selectedItem.HSNCode || selectedItem.HSN || '',
                                        VATRate: vatRate,
                                        Rate: itemRate,
                                        Quantity: quantity,
                                        ReceivedQuantity: quantity,
                                        StockQuantity: quantity,
                                        DamageQuantity: 0,
                                        DiscountPercentage: 0,
                                        DiscountAmount: 0,
                                        Amount: Math.round(amount * 100) / 100,
                                        TaxableAmount: Math.round(taxableAmount * 100) / 100,
                                        // CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
                                        // SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
                                        VATAmount: vatAmount,
                                        IGSTAmount: 0,
                                        TotalAmount: Math.round((taxableAmount + vatAmount) * 100) / 100
                                      };

                                      console.log('Updated item details:', updatedDetails[index]);
                                      setSelectedPODetails(updatedDetails);
                                      // Immediately trigger calculation after item selection
                                      calculatePOTotals(updatedDetails);
                                    }
                                  } else {
                                    // Clear selection
                                    handlePOToPurchaseDetailChange(index, 'ItemId', '');
                                    handlePOToPurchaseDetailChange(index, 'ItemName', '');
                                    handlePOToPurchaseDetailChange(index, 'HSNCode', '');
                                  }
                                }}
                                style={{
                                  width: '100%',
                                  padding: '4px',
                                  border: `1px solid ${!detail.ItemId ? '#ef4444' : '#d1d5db'}`,
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  backgroundColor: !detail.ItemId ? '#fef2f2' : 'white'
                                }}
                              >
                                <option value="">Select Item</option>
                                {items.map(item => (
                                  <option key={item.Id} value={item.Id}>{item.Name}</option>
                                ))}
                              </select>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px', color: '#6b7280' }}>
                              {detail.HSNCode || '-'}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <input
                                type="number"
                                value={detail.Quantity || ''}
                                onChange={(e) => handlePOToPurchaseDetailChange(index, 'Quantity', e.target.value)}
                                style={{ width: '60px', padding: '4px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'center', fontSize: '12px' }}
                                min="0"
                              />
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <input
                                type="number"
                                value={detail.ReceivedQuantity || ''}
                                onChange={(e) => handlePOToPurchaseDetailChange(index, 'ReceivedQuantity', e.target.value)}
                                onKeyPress={(e) => {
                                  // Prevent non-numeric characters except decimal point
                                  if (!/[0-9.]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'Enter') {
                                    e.preventDefault();
                                  }
                                }}
                                onPaste={(e) => {
                                  // Prevent pasting invalid content
                                  const paste = (e.clipboardData || window.clipboardData).getData('text');
                                  if (!/^[0-9]*\.?[0-9]*$/.test(paste)) {
                                    e.preventDefault();
                                  }
                                }}
                                style={{
                                  width: '60px',
                                  padding: '4px',
                                  border: `1px solid ${!detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0 ? '#ef4444' : '#d1d5db'}`,
                                  borderRadius: '4px',
                                  textAlign: 'center',
                                  fontSize: '12px',
                                  backgroundColor: (!detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0) ? '#fef2f2' : 'white'
                                }}
                                min="0"
                                step="0.01"
                                required
                              />
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <input
                                type="number"
                                value={detail.DiscountPercentage || 0}
                                onChange={(e) => handlePOToPurchaseDetailChange(index, 'DiscountPercentage', e.target.value)}
                                style={{ width: '60px', padding: '4px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'center', fontSize: '12px' }}
                                min="0"
                                max="100"
                                step="0.01"
                              />
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>
                              <input
                                type="number"
                                value={detail.DiscountAmount || 0}
                                onChange={(e) => handlePOToPurchaseDetailChange(index, 'DiscountAmount', e.target.value)}
                                style={{ width: '80px', padding: '4px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'right', fontSize: '12px' }}
                                min="0"
                                step="0.01"
                              />
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>
                              <input
                                type="number"
                                value={detail.Rate || ''}
                                onChange={(e) => handlePOToPurchaseDetailChange(index, 'Rate', e.target.value)}
                                onKeyPress={(e) => {
                                  // Prevent non-numeric characters except decimal point
                                  if (!/[0-9.]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'Enter') {
                                    e.preventDefault();
                                  }
                                }}
                                onPaste={(e) => {
                                  // Prevent pasting invalid content
                                  const paste = (e.clipboardData || window.clipboardData).getData('text');
                                  if (!/^[0-9]*\.?[0-9]*$/.test(paste)) {
                                    e.preventDefault();
                                  }
                                }}
                                style={{
                                  width: '80px',
                                  padding: '4px',
                                  border: `1px solid ${!detail.Rate || parseFloat(detail.Rate) <= 0 ? '#ef4444' : '#d1d5db'}`,
                                  borderRadius: '4px',
                                  textAlign: 'right',
                                  fontSize: '12px',
                                  backgroundColor: (!detail.Rate || parseFloat(detail.Rate) <= 0) ? '#fef2f2' : 'white'
                                }}
                                min="0"
                                step="0.01"
                                required
                              />
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <span style={{ fontWeight: 600, color: '#059669' }}>{detail.VATRate || 0}%</span>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{formatCurrency(detail.TaxableAmount || 0)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#dc2626' }}>{formatCurrency((detail.VATAmount || 0) + (detail.IGSTAmount || 0))}</td>
                            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#10b981' }}>{formatCurrency(detail.TotalAmount || 0)}</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const updatedDetails = selectedPODetails.filter((_, i) => i !== index);
                                  setSelectedPODetails(updatedDetails);
                                  calculatePOTotals(updatedDetails);
                                }}
                                sx={{ color: '#ef4444', p: 0.5 }}
                              >
                                <Delete sx={{ fontSize: 14 }} />
                              </IconButton>
                            </td>
                          </tr>
                        ))}
                        {selectedPODetails.length === 0 && (
                          <tr>
                            <td colSpan="13" style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No items found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              )}

              {/* Additional Charges & Summary Layout */}
              <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mt: isMobile ? 0.5 : 1 }}>
                {/* Additional Charges Section */}
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981', mb: 1, display: 'block' }}>Additional Charges</Typography>
                  <Box sx={{ border: isMobile ? 'none' : '1px solid #e2e8f0', borderRadius: isMobile ? '10px' : 1, p: 1.5, bgcolor: '#f9fafb' }}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5, fontSize: isMobile ? '0.7rem' : 'inherit' }}>Freight</Typography>
                        <input
                          type="number"
                          value={poToPurchaseFormData.FreightAmount || ''}
                          onChange={(e) => handlePOToPurchaseFormChange('FreightAmount', e.target.value)}
                          style={{ width: '100%', padding: isMobile ? '9px 10px' : '6px 8px', border: `1px solid ${isMobile ? '#e2e8f0' : '#d1d5db'}`, borderRadius: isMobile ? '6px' : '4px', fontSize: isMobile ? '14px' : '12px', textAlign: 'right' }}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5, fontSize: isMobile ? '0.7rem' : 'inherit' }}>Packing</Typography>
                        <input
                          type="number"
                          value={poToPurchaseFormData.PackingAmount || ''}
                          onChange={(e) => handlePOToPurchaseFormChange('PackingAmount', e.target.value)}
                          style={{ width: '100%', padding: isMobile ? '9px 10px' : '6px 8px', border: `1px solid ${isMobile ? '#e2e8f0' : '#d1d5db'}`, borderRadius: isMobile ? '6px' : '4px', fontSize: isMobile ? '14px' : '12px', textAlign: 'right' }}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </Grid>
                      {/* <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>TCS Amount</Typography>
                        <input
                          type="number"
                          value={poToPurchaseFormData.TCSAmount || ''}
                          onChange={(e) => handlePOToPurchaseFormChange('TCSAmount', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </Grid> */}
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5, fontSize: isMobile ? '0.7rem' : 'inherit' }}>Paid Amount</Typography>
                        <input
                          type="number"
                          value={poToPurchaseFormData.PaidAmount || ''}
                          onChange={(e) => handlePOToPurchaseFormChange('PaidAmount', e.target.value)}
                          style={{ width: '100%', padding: isMobile ? '9px 10px' : '6px 8px', border: `1px solid ${isMobile ? '#e2e8f0' : '#d1d5db'}`, borderRadius: isMobile ? '6px' : '4px', fontSize: isMobile ? '14px' : '12px', textAlign: 'right' }}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5, fontSize: isMobile ? '0.7rem' : 'inherit' }}>Remarks</Typography>
                        <textarea
                          value={poToPurchaseFormData.AdditionalRemarks || ''}
                          onChange={(e) => handlePOToPurchaseFormChange('AdditionalRemarks', e.target.value)}
                          style={{ width: '100%', padding: isMobile ? '9px 10px' : '6px 8px', border: `1px solid ${isMobile ? '#e2e8f0' : '#d1d5db'}`, borderRadius: isMobile ? '6px' : '4px', fontSize: isMobile ? '14px' : '12px', minHeight: isMobile ? '70px' : '60px', resize: 'vertical' }}
                          placeholder="Enter remarks..."
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                {/* Summary Section */}
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981', mb: 1, display: 'block' }}>Summary</Typography>
                  <Box sx={{ border: '1px solid #e2e8f0', borderRadius: isMobile ? '10px' : 1, p: 1.5, bgcolor: '#f0fdf4' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: isMobile ? 0.4 : 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Subtotal</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(poToPurchaseFormData.Amount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: isMobile ? 0.4 : 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Discount</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#dc2626' }}>{formatCurrency(poToPurchaseFormData.DiscountAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: isMobile ? 0.4 : 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Taxable</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(poToPurchaseFormData.TaxableAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: isMobile ? 0.4 : 0.5 }}>
                      <Typography variant="caption" color="text.secondary">VAT Amount</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency((parseFloat(poToPurchaseFormData.CGSTAmount) || 0) + (parseFloat(poToPurchaseFormData.SGSTAmount) || 0) + (parseFloat(poToPurchaseFormData.IGSTAmount) || 0))}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: isMobile ? 0.4 : 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Freight</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(poToPurchaseFormData.FreightAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: isMobile ? 0.4 : 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Freight VAT ({Math.round(poToPurchaseFormData.FreightGSTRate || poToPurchaseFormData.FreightVATRate || 0)}%)</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(poToPurchaseFormData.FreightGSTAmount || poToPurchaseFormData.FreightVATAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: isMobile ? 0.4 : 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Packing</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(poToPurchaseFormData.PackingAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: isMobile ? 0.4 : 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Packing VAT ({Math.round(poToPurchaseFormData.PackingGSTRate || poToPurchaseFormData.PackingVATRate || 0)}%)</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(poToPurchaseFormData.PackingGSTAmount || poToPurchaseFormData.PackingVATAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: isMobile ? 0.4 : 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Round Off</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>{(poToPurchaseFormData.RoundOff || 0) >= 0 ? '+' : ''}{formatCurrency(Math.abs(poToPurchaseFormData.RoundOff || 0))}</Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: isMobile ? 0.4 : 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981' }}>Total</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981', fontSize: '0.9rem' }}>{formatCurrency(poToPurchaseFormData.TotalAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: isMobile ? 0.4 : 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Paid</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#10b981' }}>{formatCurrency(poToPurchaseFormData.PaidAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: isMobile ? 0.4 : 0.5, borderTop: '1px solid #d1fae5', pt: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626' }}>Balance</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.9rem' }}>{formatCurrency((poToPurchaseFormData.TotalAmount || 0) - (poToPurchaseFormData.PaidAmount || 0))}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{
          px: isMobile ? 1.5 : 2, py: isMobile ? 1.25 : 1.5, borderTop: '1px solid #e2e8f0', bgcolor: isMobile ? '#fff' : '#f0fdf4',
          position: isMobile ? 'fixed' : 'relative',
          bottom: isMobile ? 0 : 'auto',
          left: isMobile ? 0 : 'auto',
          right: isMobile ? 0 : 'auto',
          zIndex: isMobile ? 20 : 'auto',
          boxShadow: isMobile ? '0 -2px 8px rgba(0,0,0,0.06)' : 'none',
          gap: 1,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end'
        }}>
          <Button onClick={handleClosePOToPurchaseModal} variant="outlined" size="small" sx={{
            borderColor: isMobile ? '#e2e8f0' : '#10b981', color: isMobile ? '#64748b' : '#10b981',
            '&:hover': { borderColor: isMobile ? '#cbd5e1' : '#059669', bgcolor: isMobile ? '#f8fafc' : '#d1fae5' },
            minWidth: '80px', fontSize: '0.8rem', borderRadius: isMobile ? '8px' : '4px', py: 0.75, textTransform: 'none', fontWeight: 500
          }}>
            Cancel
          </Button>
          <Button onClick={handleSavePOToPurchase} variant="contained" size="small" disabled={loading} sx={{
            bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' },
            minWidth: isMobile ? '140px' : '120px', borderRadius: isMobile ? '8px' : '4px', py: 0.75,
            fontWeight: 600, fontSize: '0.8rem', boxShadow: isMobile ? '0 2px 4px rgba(16,185,129,0.3)' : 'none', textTransform: 'none'
          }}>
            Create Purchase
          </Button>
        </DialogActions>
      </Dialog >

      {/* PO Details View Modal */}
      < Dialog
        open={poDetailsModalOpen}
        onClose={handleClosePODetailsModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '90vh', maxWidth: '1000px' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f0f9ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment sx={{ color: '#0ea5e9', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Purchase Order Details
              </Typography>
              {poDetailsData && (
                <Chip label={poDetailsData.PurchaseOrderNo} size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#dbeafe', color: '#1e40af' }} />
              )}
            </Box>
            <IconButton onClick={handleClosePODetailsModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {poDetailsData && (
            <>
              {/* PO Header Information */}
              <Box sx={{ p: 1.5, bgcolor: '#f0f9ff', borderRadius: 1, mb: 2, border: '1px solid #dbeafe' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>PO Number</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af' }}>{poDetailsData.PurchaseOrderNo || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>PO Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(poDetailsData.PurchaseOrderDate)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Supplier</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>{poDetailsData.MasterName || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Total Amount</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626' }}>{formatCurrency(poDetailsData.TotalAmount, poDetailsData.CurrencyName)}</Typography>
                  </Grid>

                  {/* Second Row */}
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Remarks</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', wordBreak: 'break-word' }}>
                      {poDetailsData.Remarks || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Instruction 1</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', wordBreak: 'break-word' }}>
                      {poDetailsData.Instruction1 || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Instruction 2</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', wordBreak: 'break-word' }}>
                      {poDetailsData.Instruction2 || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* PO Items Table */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#0ea5e9', mb: 1, display: 'block' }}>
                PO Items ({poDetailsItems.length} items)
              </Typography>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: '#dbeafe' }}>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>#</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#1e40af' }}>Item</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>QTY</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>Rate</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>Amount</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>VAT%</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>VAT Amt</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {poDetailsItems.map((item, index) => (
                        <tr key={item.Id || index} style={{ borderBottom: '1px solid #e2e8f0', background: index % 2 === 0 ? '#fff' : '#f8fafc' }}>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{index + 1}</td>
                          <td style={{ padding: '8px', fontWeight: 500 }}>{item.ItemName || '-'}</td>
                          <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{item.Quantity || 0}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(item.Rate || 0, poDetailsData?.CurrencyName)}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{formatCurrency(item.Amount || 0, poDetailsData?.CurrencyName)}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <span style={{ fontWeight: 600, color: '#dc2626' }}>{item.VATRate || 0}%</span>
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#dc2626' }}>
                            {formatCurrency((item.VATAmount || 0), poDetailsData?.CurrencyName)}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: '#1e40af' }}>{formatCurrency(item.TotalAmount || 0, poDetailsData?.CurrencyName)}</td>
                        </tr>
                      ))}
                      {poDetailsItems.length === 0 && (
                        <tr>
                          <td colSpan="8" style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No items found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* PO Summary */}
              <Box sx={{ p: 1, bgcolor: '#f0f9ff', borderRadius: 1, border: '1px solid #dbeafe' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#0ea5e9', mb: 0.5, display: 'block' }}>PO Summary</Typography>
                <Grid container spacing={1}>
                  {/* First Row */}
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Subtotal</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.Amount || 0, poDetailsData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Discount</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.DiscountAmount || 0, poDetailsData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Taxable</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.TaxableAmount || 0, poDetailsData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>VAT</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency((poDetailsData.VATAmount || 0), poDetailsData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Freight</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.FreightAmount || 0, poDetailsData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Second Row */}
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Freight VAT</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.FreightVATAmount || 0, poDetailsData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Packing</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.PackingAmount || 0, poDetailsData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Packing VAT</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.PackingVATAmount || 0, poDetailsData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>TCS</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.TCSAmount || 0, poDetailsData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: '#dbeafe', borderRadius: 1, border: '1px solid #93c5fd' }}>
                      <Typography variant="caption" sx={{ color: '#1e40af', display: 'block', mb: 0.2, fontWeight: 600, fontSize: '0.7rem' }}>Total</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e40af', fontSize: '0.85rem' }}>
                        {formatCurrency(poDetailsData.TotalAmount || 0, poDetailsData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Third Row - Additional Details */}
                  {/* <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>VAT Amount</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.VATAmount || 0, poDetailsData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>SGST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.SGSTAmount || 0, poDetailsData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>IGST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.IGSTAmount || 0, poDetailsData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid> */}
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Round Off</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {(poDetailsData.RoundOff || 0) >= 0 ? '+' : ''}{formatCurrency(Math.abs(poDetailsData.RoundOff || 0), poDetailsData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Paid Amount</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.PaidAmount || 0, poDetailsData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f0f9ff' }}>
          <Button onClick={handleClosePODetailsModal} variant="contained" size="small" sx={{ bgcolor: '#0ea5e9', '&:hover': { bgcolor: '#0284c7' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog >

      {/* Supplier Drill-down Modal */}
      < Dialog open={supplierModalOpen} onClose={handleCloseSupplierModal} maxWidth="md" fullWidth >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Purchases - {selectedSupplier?.name}</Typography>
          <IconButton onClick={handleCloseSupplierModal}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {supplierPurchases.length === 0 ? (
            <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>No purchases found for this supplier</Typography>
          ) : (
            supplierPurchases.map((purchase) => (
              <Box key={purchase.Id} sx={{ mb: 2, border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => togglePurchaseExpand(purchase.Id)}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e40af' }}>{purchase.BillNumber}</Typography>
                    <Typography variant="caption" color="textSecondary">{formatDate(purchase.BillDate)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#059669' }}>{formatCurrency(purchase.TotalAmount, purchase.CurrencyName)}</Typography>
                    {expandedPurchaseId === purchase.Id ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                </Box>
                <Collapse in={expandedPurchaseId === purchase.Id}>
                  <Box sx={{ p: 2, bgcolor: '#fff' }}>
                    {(purchase.PurchaseDetails || []).map((d, idx) => (
                      <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: idx < (purchase.PurchaseDetails?.length || 0) - 1 ? '1px solid #f3f4f6' : 'none' }}>
                        <Typography variant="body2">{d.ItemName || items.find(i => i.Id === d.ItemId)?.Name || 'Item'}</Typography>
                        <Typography variant="body2" color="textSecondary">{d.ReceivedQuantity} × {formatCurrency(d.Rate)}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ))
          )}
        </DialogContent>
      </Dialog >

      {/* Good Receipt Modal */}
      {
        verifyModalOpen && (
          <VerifyPurchaseModal
            open={verifyModalOpen}
            onClose={handleCloseVerifyModal}
            formData={verifyPurchaseData}
            purchaseDetails={verifyPurchaseDetails}
            onVerify={handleUpdateFromGoodReceipt}
            onActualVerify={handleActualVerify}
            suppliers={suppliers}
            items={items}
            loading={loading}
          />
        )
      }

      {/* Purchase Return Modal */}
      <Dialog
        open={purchaseReturnModalOpen}
        onClose={handleClosePurchaseReturnModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '90vh',
            maxWidth: { xs: '95vw', sm: '90vw', md: '1400px' },
            width: '100%'
          }
        }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#fef3c7' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment sx={{ color: '#f59e0b', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Create Purchase Return
              </Typography>
              {purchaseReturnData && (
                <Chip
                  label={`From: ${purchaseReturnData.PurchaseNo || purchaseReturnData.BillNumber || 'N/A'}`}
                  size="small"
                  sx={{
                    fontSize: '0.65rem',
                    height: '20px',
                    bgcolor: '#dbeafe',
                    color: '#1e40af',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: '#bfdbfe'
                    }
                  }}
                  onClick={() => handleOpenPurchaseDetailsViewModal(purchaseReturnData)}
                />
              )}
            </Box>
            <IconButton onClick={handleClosePurchaseReturnModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {purchaseReturnData && (
            <>
              {/* Return Information */}
              <Box sx={{
                p: 1,
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: 1.5,
                mb: 2,
                border: '1px solid #f59e0b',
                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.1)'
              }}>
                <Grid container spacing={1}>
                  {/* Input Fields Row */}
                  <Grid item xs={6} sm={2.5}>
                    <Typography variant="caption" sx={{
                      color: '#374151',
                      display: 'block',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px'
                    }}>
                      R.No *
                    </Typography>
                    <input
                      type="text"
                      value={purchaseReturnFormData.ReturnNumber || ''}
                      onChange={(e) => handlePurchaseReturnFormChange('ReturnNumber', e.target.value)}
                      readOnly
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        backgroundColor: '#f9fafb',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                        cursor: 'not-allowed',
                        color: '#6b7280',
                        fontFamily: 'inherit'
                      }}
                      placeholder="Auto-generated"
                    />
                  </Grid>

                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" sx={{
                      color: '#374151',
                      display: 'block',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px'
                    }}>
                      R.Date *
                    </Typography>
                    <input
                      type="date"
                      value={purchaseReturnFormData.ReturnDate || ''}
                      onChange={(e) => handlePurchaseReturnFormChange('ReturnDate', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        backgroundColor: '#ffffff',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                        fontFamily: 'inherit'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#f59e0b';
                        e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <Typography variant="caption" sx={{
                      color: '#374151',
                      display: 'block',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px'
                    }}>
                      VAT Type *
                    </Typography>
                    <select
                      value={purchaseReturnFormData.VATCalculationType || 1}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        handlePurchaseReturnFormChange('VATCalculationType', value);
                        handlePurchaseReturnFormChange('VATCalculationTypeName', value === 1 ? 'Excluding Tax' : 'Including Tax');
                      }}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        backgroundColor: '#ffffff',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                        fontFamily: 'inherit',
                        cursor: 'pointer'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#f59e0b';
                        e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value={1}>Excluding Tax</option>
                      <option value={2}>Including Tax</option>
                    </select>
                  </Grid>

                  <Grid item xs={6} sm={1.5}>
                    <Typography variant="caption" sx={{
                      color: '#374151',
                      display: 'block',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px'
                    }}>
                      Currency
                    </Typography>
                    <input
                      type="text"
                      value={purchaseReturnFormData.CurrencyName || 'AED'}
                      readOnly
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        backgroundColor: '#f9fafb',
                        outline: 'none',
                        cursor: 'not-allowed',
                        color: '#6b7280',
                        fontFamily: 'inherit'
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{
                      color: '#374151',
                      display: 'block',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px'
                    }}>
                      Remarks
                    </Typography>
                    <input
                      type="text"
                      value={purchaseReturnFormData.Remarks || ''}
                      onChange={(e) => handlePurchaseReturnFormChange('Remarks', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        backgroundColor: '#ffffff',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                        fontFamily: 'inherit'
                      }}
                      placeholder="Enter return remarks..."
                      onFocus={(e) => {
                        e.target.style.borderColor = '#f59e0b';
                        e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Return Items Table */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>Return Items</Typography>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
                {purchaseReturnDetails.length > 0 ? (
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '0.8rem'
                    }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr style={{ background: '#fed7aa' }}>
                          <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#ea580c', minWidth: '35px', fontSize: '0.75rem' }}>#</th>
                          <th style={{ padding: '8px 6px', textAlign: 'left', fontWeight: 600, color: '#ea580c', minWidth: '120px', fontSize: '0.75rem' }}>Item</th>
                          <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#ea580c', minWidth: '70px', fontSize: '0.75rem' }}>HSN</th>
                          <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#ea580c', minWidth: '75px', fontSize: '0.75rem' }}>Purchased</th>
                          <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#ea580c', minWidth: '85px', fontSize: '0.75rem' }}>Return Qty *</th>
                          <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#ea580c', minWidth: '75px', fontSize: '0.75rem' }}>Rate</th>
                          <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#ea580c', minWidth: '80px', fontSize: '0.75rem' }}>Amount</th>
                          <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#ea580c', minWidth: '70px', fontSize: '0.75rem' }}>Disc %</th>
                          <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#ea580c', minWidth: '80px', fontSize: '0.75rem' }}>Disc Amt</th>
                          <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#ea580c', minWidth: '60px', fontSize: '0.75rem' }}>VAT%</th>
                          <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#ea580c', minWidth: '80px', fontSize: '0.75rem' }}>Taxable</th>
                          <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#ea580c', minWidth: '90px', fontSize: '0.75rem' }}>VAT Amount</th>
                          <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#ea580c', minWidth: '85px', fontSize: '0.75rem' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchaseReturnDetails.map((detail, index) => (
                          <tr key={detail.Id || index} style={{
                            borderBottom: '1px solid #e2e8f0',
                            background: index % 2 === 0 ? '#fff' : '#fefbf3'
                          }}>
                            <td style={{ padding: '8px 4px', textAlign: 'center', fontSize: '0.75rem' }}>{index + 1}</td>
                            <td style={{ padding: '8px 6px', fontWeight: 500, fontSize: '0.75rem' }}>
                              {detail.ItemName || items.find(i => i.Id === detail.ItemId)?.Name || '-'}
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'center', fontSize: '0.7rem', color: '#6b7280' }}>
                              {detail.HSNCode || '-'}
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#6b7280', fontSize: '0.75rem' }}>
                              {detail.PreviousStockQuantity > 0 ? detail.PreviousStockQuantity : (detail.PurchaseQuantity || 0)}
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                              <input
                                type="number"
                                value={detail.ReturnQuantity || ''}
                                onChange={(e) => handlePurchaseReturnDetailChange(index, 'ReturnQuantity', e.target.value)}
                                style={{
                                  width: '70px',
                                  padding: '4px',
                                  border: `1px solid ${(detail.ReturnQuantity || 0) > (detail.MaxReturnQuantity || 0) ? '#ef4444' : '#d1d5db'}`,
                                  borderRadius: '4px',
                                  textAlign: 'center',
                                  fontSize: '0.75rem',
                                  backgroundColor: (detail.ReturnQuantity || 0) > (detail.MaxReturnQuantity || 0) ? '#fef2f2' : 'white'
                                }}
                                min="0"
                                max={detail.MaxReturnQuantity || 0}
                                step="0.01"
                                placeholder="0"
                              />
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem' }}>
                              {formatCurrency(detail.Rate || 0)}
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: (detail.ReturnQuantity || 0) > 0 ? '#374151' : '#9ca3af', fontSize: '0.75rem' }}>
                              {formatCurrency(detail.ReturnAmount || 0)}
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                              <input
                                type="number"
                                value={detail.DiscountPercentage || ''}
                                onChange={(e) => handlePurchaseReturnDetailChange(index, 'DiscountPercentage', e.target.value)}
                                style={{
                                  width: '60px',
                                  padding: '4px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  textAlign: 'right',
                                  fontSize: '0.75rem',
                                  backgroundColor: 'white'
                                }}
                                min="0"
                                max="100"
                                step="0.01"
                                placeholder="0"
                              />
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                              <input
                                type="number"
                                value={detail.ReturnDiscountAmount || ''}
                                onChange={(e) => handlePurchaseReturnDetailChange(index, 'DiscountAmount', e.target.value)}
                                style={{
                                  width: '70px',
                                  padding: '4px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  textAlign: 'right',
                                  fontSize: '0.75rem',
                                  backgroundColor: 'white'
                                }}
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                              />
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                              <span style={{ fontWeight: 600, color: '#059669', fontSize: '0.75rem' }}>{detail.VATRate || detail.GSTRate || 0}%</span>
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: (detail.ReturnQuantity || 0) > 0 ? '#059669' : '#9ca3af', fontSize: '0.75rem' }}>
                              {formatCurrency(detail.ReturnTaxableAmount || 0)}
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: (detail.ReturnQuantity || 0) > 0 ? '#dc2626' : '#9ca3af', fontSize: '0.75rem' }}>
                              {formatCurrency(detail.ReturnVATAmount || 0)}
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: (detail.ReturnQuantity || 0) > 0 ? '#10b981' : '#9ca3af', fontSize: '0.75rem' }}>
                              {formatCurrency(detail.ReturnTotalAmount || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center', color: '#6b7280' }}>
                    <Typography variant="body2">
                      {purchaseReturnData ?
                        'No items found in this purchase. Please check the purchase details.' :
                        'Loading purchase items...'
                      }
                    </Typography>
                    {purchaseReturnData && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#9ca3af' }}>
                        Purchase ID: {purchaseReturnData.Id} | Bill Number: {purchaseReturnData.BillNumber}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              {/* Return Summary */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>Return Summary</Typography>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                <Grid container>
                  {/* Left Side - Editable Amount Fields */}
                  <Grid item xs={12} md={4} sx={{ p: 1, borderRight: { md: '1px solid #e2e8f0' } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>Additional Charges</Typography>

                    {/* First Row - Freight and Packing */}
                    <Grid container spacing={1} sx={{ mb: 1 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{
                          color: '#6b7280',
                          display: 'block',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          mb: 0.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Freight Amount
                        </Typography>
                        <Typography variant="caption" sx={{
                          color: '#059669',
                          display: 'block',
                          fontWeight: 500,
                          fontSize: '0.65rem',
                          mb: 0.5
                        }}>
                          VAT Rate: {purchaseReturnFormData.FreightVATRate || 5}%
                        </Typography>
                        <input
                          type="number"
                          value={purchaseReturnFormData.FreightAmount || ''}
                          onChange={(e) => handlePurchaseReturnFormChange('FreightAmount', parseFloat(e.target.value) || 0)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            backgroundColor: 'white',
                            outline: 'none',
                            textAlign: 'right'
                          }}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          onFocus={(e) => {
                            e.target.style.borderColor = '#f59e0b';
                            e.target.style.boxShadow = '0 0 0 2px rgba(245, 158, 11, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{
                          color: '#6b7280',
                          display: 'block',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          mb: 0.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Packing Amount
                        </Typography>
                        <Typography variant="caption" sx={{
                          color: '#059669',
                          display: 'block',
                          fontWeight: 500,
                          fontSize: '0.65rem',
                          mb: 0.5
                        }}>
                          VAT Rate: {purchaseReturnFormData.PackingVATRate || 5}%
                        </Typography>
                        <input
                          type="number"
                          value={purchaseReturnFormData.PackingAmount || ''}
                          onChange={(e) => handlePurchaseReturnFormChange('PackingAmount', parseFloat(e.target.value) || 0)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            backgroundColor: 'white',
                            outline: 'none',
                            textAlign: 'right'
                          }}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          onFocus={(e) => {
                            e.target.style.borderColor = '#f59e0b';
                            e.target.style.boxShadow = '0 0 0 2px rgba(245, 158, 11, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </Grid>
                    </Grid>

                    {/* Second Row - TCS and Received Amount */}
                    <Grid container spacing={1}>
                      {/* <Grid item xs={6}>
                        <Typography variant="caption" sx={{
                          color: '#6b7280',
                          display: 'block',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          mb: 0.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          TCS Amount
                        </Typography>
                        <input
                          type="number"
                          value={purchaseReturnFormData.TCSAmount || ''}
                          onChange={(e) => handlePurchaseReturnFormChange('TCSAmount', parseFloat(e.target.value) || 0)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            backgroundColor: 'white',
                            outline: 'none',
                            textAlign: 'right'
                          }}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          onFocus={(e) => {
                            e.target.style.borderColor = '#f59e0b';
                            e.target.style.boxShadow = '0 0 0 2px rgba(245, 158, 11, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </Grid> */}

                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{
                          color: '#6b7280',
                          display: 'block',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          mb: 0.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Paid Amount
                        </Typography>
                        <input
                          type="number"
                          value={purchaseReturnFormData.PaidAmount || ''}
                          onChange={(e) => handlePurchaseReturnFormChange('PaidAmount', parseFloat(e.target.value) || 0)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            backgroundColor: 'white',
                            outline: 'none',
                            textAlign: 'right'
                          }}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          onFocus={(e) => {
                            e.target.style.borderColor = '#f59e0b';
                            e.target.style.boxShadow = '0 0 0 2px rgba(245, 158, 11, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Right Side - Calculated Summary */}
                  <Grid item xs={12} md={8} sx={{ p: 1, bgcolor: '#fefbf3' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>Return Calculation</Typography>

                    {/* Calculation Cards Layout */}
                    <Grid container spacing={0.8}>
                      {/* Subtotal */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Subtotal
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                            {formatCurrency((purchaseReturnFormData.Amount || 0))}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Discount */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Discount
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                            {formatCurrency((purchaseReturnFormData.DiscountAmount || 0))}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Taxable */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Taxable
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                            {formatCurrency((purchaseReturnFormData.TaxableAmount || 0))}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* VAT Total */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            VAT Total
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                            {formatCurrency(((purchaseReturnFormData.VATAmount || 0) + (purchaseReturnFormData.IGSTAmount || 0)))}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Freight */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Freight
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                            {formatCurrency((purchaseReturnFormData.FreightAmount || 0))}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Freight GST */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Freight VAT
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                            {formatCurrency((purchaseReturnFormData.FreightVATAmount || 0))}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Packing */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Packing
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                            {formatCurrency((purchaseReturnFormData.PackingAmount || 0))}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Packing GST */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Packing VAT
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                            {formatCurrency((purchaseReturnFormData.PackingVATAmount || 0))}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* TCS */}
                      {/* <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            TCS
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                            {formatCurrency((purchaseReturnFormData.TCSAmount || 0))}
                          </Typography>
                        </Box>
                      </Grid> */}

                      {/* Round Off */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Round Off
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                            {(purchaseReturnFormData.RoundOff || 0) >= 0 ? '+' : ''}{formatCurrency((purchaseReturnFormData.RoundOff || 0))}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Total Amount and Balance Amount - Side by Side */}
                    <Grid container spacing={0.8} sx={{ mt: 1 }}>
                      <Grid item xs={6} sm={6}>
                        <Box sx={{ p: 1, bgcolor: '#fed7aa', borderRadius: 1, border: '2px solid #f59e0b', textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ color: '#ea580c', display: 'block', mb: 0.1, fontSize: '0.75rem', fontWeight: 600 }}>Return Total Amount</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#ea580c', fontSize: '1.1rem' }}>
                            {formatCurrency((purchaseReturnFormData.TotalAmount || 0))}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={6} sm={6}>
                        <Box sx={{ p: 1, bgcolor: '#e0f2fe', borderRadius: 1, border: '1px solid #0ea5e9', textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ color: '#0369a1', display: 'block', mb: 0.1, fontSize: '0.75rem', fontWeight: 600 }}>Balance Amount</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0369a1', fontSize: '1rem' }}>
                            {formatCurrency(((purchaseReturnFormData.TotalAmount || 0) - (purchaseReturnFormData.PaidAmount || 0)))}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#fef3c7' }}>
          <Button onClick={handleClosePurchaseReturnModal} variant="outlined" size="small" sx={{ borderColor: '#f59e0b', color: '#f59e0b', '&:hover': { borderColor: '#d97706', bgcolor: '#fed7aa' } }}>
            Cancel
          </Button>
          <Button onClick={handleSavePurchaseReturn} variant="contained" size="small" disabled={loading} sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
            Create Return
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purchase Details View Modal (for bill number click) */}
      <Dialog
        open={purchaseDetailsViewModalOpen}
        onClose={handleClosePurchaseDetailsViewModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '80vh' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Visibility sx={{ color: '#0ea5e9', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Purchase Details
              </Typography>
              {purchaseDetailsViewData && (
                <Chip label={purchaseDetailsViewData.BillNumber} size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: ' #dbeafe', color: ' #1e40af' }} />
              )}
            </Box>
            <IconButton onClick={handleClosePurchaseDetailsViewModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {purchaseDetailsViewData && (
            <>
              {/* Purchase Information */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Supplier</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                    {purchaseDetailsViewData.MasterName || getSupplierName(purchaseDetailsViewData)}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(purchaseDetailsViewData.BillDate)}</Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Total Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e40af' }}>{formatCurrency(purchaseDetailsViewData.TotalAmount, purchaseDetailsViewData.CurrencyName)}</Typography>
                </Box>
                {purchaseDetailsViewData.PurchaseOrderNo && (
                  <Box sx={{ minWidth: 120 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>PO Number</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{purchaseDetailsViewData.PurchaseOrderNo}</Typography>
                  </Box>
                )}
              </Box>

              {/* Purchase Items */}
              {purchaseDetailsViewItems.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>Items</Typography>
                  <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                          <tr style={{ background: '#dbeafe' }}>
                            <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>#</th>
                            <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#1e40af' }}>Item</th>
                            <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>Qty</th>
                            <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>Damage Qty</th>
                            <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>Stock Qty</th>
                            <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>Rate</th>
                            <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>GST%</th>
                            <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {purchaseDetailsViewItems.map((item, index) => (
                            <tr key={item.Id || index} style={{
                              borderBottom: '1px solid #e2e8f0',
                              background: index % 2 === 0 ? '#fff' : '#f8fafc'
                            }}>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{index + 1}</td>
                              <td style={{ padding: '8px', fontWeight: 500 }}>
                                {item.ItemName || items.find(i => i.Id === item.ItemId)?.Name || '-'}
                                {item.HSNCode && (
                                  <div style={{ fontSize: '11px', color: '#6b7280' }}>HSN: {item.HSNCode}</div>
                                )}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>
                                {item.ReceivedQuantity || item.Quantity || 0}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#dc2626' }}>
                                {item.DamageQuantity || 0}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#059669' }}>
                                {item.StockQuantity || 0}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>
                                {formatCurrency(item.Rate || 0)}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>
                                <span style={{ fontWeight: 600, color: '#059669' }}>{item.GSTRate || 0}%</span>
                              </td>
                              <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
                                {formatCurrency(item.TotalAmount || 0, purchaseDetailsViewData.CurrencyName)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  </Box>
                </>
              )}

              {/* Purchase Summary */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>Summary</Typography>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 2, bgcolor: '#f8fafc' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Subtotal</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency(purchaseDetailsViewData.Amount || 0, purchaseDetailsViewData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>GST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency((purchaseDetailsViewData.CGSTAmount || 0) + (purchaseDetailsViewData.SGSTAmount || 0) + (purchaseDetailsViewData.IGSTAmount || 0), purchaseDetailsViewData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Other Charges</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency((purchaseDetailsViewData.FreightAmount || 0) + (purchaseDetailsViewData.PackingAmount || 0) + (purchaseDetailsViewData.TCSAmount || 0), purchaseDetailsViewData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Total</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e40af', fontSize: '0.9rem' }}>
                        {formatCurrency(purchaseDetailsViewData.TotalAmount || 0, purchaseDetailsViewData.CurrencyName)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f0f9ff' }}>
          <Button onClick={handleClosePurchaseDetailsViewModal} variant="outlined" size="small" sx={{ borderColor: '#0ea5e9', color: '#0ea5e9', '&:hover': { borderColor: '#0284c7', bgcolor: '#dbeafe' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Loading Spinner */}
      {modalLoading && <LoadingOverlay message="Loading..." />}
    </div >
  );
};

export default PurchaseIndex;
